import { apiEndpoint } from './consts';
import { assertOk } from './api-error';
import type { AccountKpiTargetsUpsert, AccountKpiTargetsWire } from '@/lib/strategy/api';
import {
  Location as LocationType,
  CampaignCreate,
  CompetitorCampaignCreate,
  CompetitorCampaignResponse,
  AddCompetitorsToCampaignResponse,
  CampaignData,
  CampaignProximityInput,
  CampaignUpdate,
  BudgetUpdate,
  SiteLink,
  CalloutType,
  AccountCallout,
  AccountCalloutListResponse,
  AccountSitelink,
  AccountSitelinkListResponse,
  NegativeKeywordList,
  NegativeKeywordListsResponse,
  SharedNegativeKeywordsResponse,
  AppliedCampaignsResponse,
  IpExclusionListResponse,
  CampaignLanguageListResponse,
  ExcludedLocationListResponse,
  StructuredSnippetType,
  PriceType,
  PromotionType,
  Ad,
  AdUpdate,
  GoogleAdsAccount,
  TaskTrackingResponse,
  User,
  CampaignRecommendations,
  GoogleCampaign,
  CAMPAIGN_STATE,
  OrganizationDataWithMembership,
  OrganizationCreate,
  OrganizationDataWithUsersMembership,
  OrganizationData,
  BudgetStatusResponse,
  ActionLog,
  LandingPage,
  LandingPageLead,
  PerformanceData,
  Business,
  BusinessCreate,
  ConversionTrackingSettings,
  ConversionAction,
  ConversionActionListResponse,
  WebsiteConversionCreateRequest,
  CallConversionCreateRequest,
  ConversionActionUpdateRequest,
  ActionFilter,
  PaginationParams,
  ActionLogResponse,
  CampaignActionsResponse,
  NotificationFilter,
  NotificationResponse,
  NotificationItem,
  CancelSubscriptionResponse,
  InvoiceHistoryResponse,
  HubSpotIntegrationStatus,
  HubSpotIntegrationResponse,
  HubSpotOAuthUrlResponse,
  HubSpotCallbackRequest,
  HubSpotAuditResponse,
  MetaIntegrationStatus,
  MetaIntegrationResponse,
  MetaOAuthUrlResponse,
  MetaCallbackRequest,
  MetaAdAccountInfo,
  MetaCampaignResponse,
  MetaCampaignDetailResponse,
  MetaCampaignOverviewResponse,
  MetaCreativePerformanceResponse,
  MetaTimeSeriesResponse,
  MetaAdSetPerformanceResponse,
  MetaBudgetPacingResponse,
  MetaPlacementResponse,
  MetaPageInfo,
  MetaPageSelectRequest,
  MetaPixelInfo,
  MetaPixelSelectRequest,
  MetaCampaignOutcome,
  MetaLeadgenFormInfo,
  MetaPhaseCampaignRequest,
  MetaAdSetBudgetResponse,
  MetaCampaignBudgetResponse,
  MetaCampaignGenerationProgress,
  MetaCampaignStatusRequest,
  MetaCampaignStatusResponse,
  MetaAdCopyUpdateRequest,
  MetaAdStatusUpdateRequest,
  MetaAdResponse,
  MetaAdSetResponse,
  MetaBudgetRecommendationResponse,
  MetaCampaignBiddingUpdateRequest,
  MetaConversionEventOption,
  MetaConversionSettingsUpdateRequest,
  MetaCustomAudienceRef,
  MetaInterestEntry,
  MetaAdPreviewResponse,
  MetaAdSetCreateRequest,
  MetaAdSetDispatchResponse,
  MetaBudgetModeUpdateRequest,
  MetaCampaignTargetingUpdateRequest,
  MetaLocaleEntry,
  MetaConversionMappingRow,
  MetaConversionsResponse,
  BudgetUpdateRequest,
  MetaCampaignDispatchResponse,
  MetaCampaignTaskStatusResponse,
  PipelineConfigCreateRequest,
  PipelineConfigResponse,
  UserProfile,
  GoogleAdsAccountUser,
  BackfillTaskResponse,
  AuthResponse,
  InvitationResponse,
  TcpaRegenerationResponse,
  ProcessRecommendationsResponse,
  KeywordOperationResponse,
  AccountDeletionResponse,
  UserNotificationSettings,
  UserNotificationSettingsUpdate,
  UserSettings,
  UserSettingsUpdate,
  OptimisationConfig,
  OptimisationConfigUpdate,
  OptimisationHistoryResponse,
  OptimisationOpportunity,
  BusinessTaskTrackingResponse,
  CampaignHeadlineMetricsResponse,
  CampaignDescriptionMetricsResponse,
  ExternalCampaignSummary,
  ExternalCampaignDetail,
  CampaignStateUpdateAccepted,
  CampaignDriftError,
  GoogleAdsConnectionBrokenError,
  GoogleIntegrationResponse,
  GeoTargetingModeResponse,
  LocationTargetingMode,
  BusinessLocationCreate,
  BusinessLocationRead,
  BusinessLocationReconcileItem,
  GeocodeResultRead,
  BusinessLocationUpdate,
  LocationTaskTriggerResponse,
  LocationTaskStatusRead,
  TrackingSnippet,
  OptimisationCampaignCreatePayload,
  OptimisationCampaignCreateAccepted,
  CampaignKeywordWithPerf,
  CreativeAsset,
  CreativeAssetTag,
  CreativeAssetGenerateParams,
  CreativeAssetListParams,
  CreativeAssetListResponse,
  CreativeAssetZipUploadResponse,
  AdLabel,
  HeadlineType,
} from './types';
import { getCookie } from './auth';
import { redirectToSignin } from '@/lib/auth-redirect';

const fetchWithAuth = async (url: string, options = {}) => {
  const token = getCookie('access_token');

  // Skip the default ``Content-Type: application/json`` when the body is ``FormData``. The
  // browser sets ``multipart/form-data; boundary=…`` automatically for FormData bodies, and
  // forcing JSON here makes FastAPI fail multipart parsing → 422 with an array-shaped error
  // detail. User-reported regression on the asset uploader.
  const body = (options as { body?: unknown }).body;
  const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData;

  let headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }
  if ('headers' in options && typeof options.headers === 'object') {
    headers = {
      ...headers,
      ...options.headers,
    };
  }

  // Add a default timeout of 30 seconds
  const controller = new AbortController();
  const fetchStartTime = performance.now(); // Move this outside try block

  // Forward caller-provided abort signal (e.g. autosave debounce/cancellation)
  // by linking it to the internal timeout controller.
  const callerSignal: AbortSignal | undefined = (options as { signal?: AbortSignal }).signal;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  // Per-call timeout override (#4662). Defaults to 30s; slow LLM-backed endpoints (e.g.
  // POST /meta/creative/suggest-copy, whose OpenAI call can take ~28s) pass a larger ``timeoutMs``
  // so the client doesn't abort a request the backend is still completing.
  const timeoutMs = (options as { timeoutMs?: number }).timeoutMs ?? 30000;
  // Distinguishes our internal timeout from a deliberate caller abort: both surface as an
  // ``AbortError``, but only the former should be rebranded as a timeout (#4997).
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    console.error(`[fetchWithAuth] Request timeout for ${url}`);
    console.error(`[fetchWithAuth] Request headers:`, headers);
    console.error(`[fetchWithAuth] Request options:`, options);
    controller.abort();
  }, timeoutMs);

  // ``timeoutMs`` is ours, not a fetch option — strip it so it never reaches ``fetch`` (#4662).
  const fetchOptions = { ...options };
  delete (fetchOptions as { timeoutMs?: number }).timeoutMs;
  const config = {
    ...fetchOptions,
    headers,
    signal: controller.signal,
  };

  try {
    const response = await fetch(url, config);

    clearTimeout(timeoutId);

    // Check for authentication errors — only 401 (unauthenticated) triggers logout
    // 403 (forbidden) means authenticated but lacks permission — propagate to caller
    if (response.status === 401) {
      redirectToSignin();
    }

    // Check for "User not found" errors (returns 404 but indicates invalid token)
    if (response.status === 404) {
      try {
        const errorData = await response.clone().json();
        if (errorData.detail === 'User not found') {
          redirectToSignin();
        }
      } catch (parseError) {
        // If we can't parse JSON, it's a different kind of 404, let it through
      }
    }

    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    const fetchEndTime = performance.now();
    console.error(`[fetchWithAuth] Request to ${url} failed after ${(fetchEndTime - fetchStartTime).toFixed(2)}ms`);

    if (error instanceof Error && error.name === 'AbortError') {
      if (timedOut) {
        console.error(`[fetchWithAuth] Request aborted due to timeout: ${url}`);
        throw new Error(`Request timeout after ${Math.round(timeoutMs / 1000)} seconds: ${url}`);
      }
      // Genuine caller abort (e.g. autosave cancellation) — re-throw unchanged so callers
      // (and React Query) see a cancellation, not a fake timeout (#4997).
      throw error;
    }

    console.error(`[fetchWithAuth] Error details:`, error);
    throw error;
  }
};

export const fetchNegativeKeywordsReport = async (campaignSlug: string, days: number) => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignSlug}/negative-keywords/report/${days}`);
  await assertOk(response);
  return response.json();
};

export const fetchDetectorKeywordsReport = async (campaignSlug: number, detector: string, threshold?: number) => {
  let url = `${apiEndpoint}/admin/campaigns/${campaignSlug}/recommendations/${detector}`;
  if (detector === 'high_cpc_keywords' && threshold !== undefined) {
    url += `?threshold=${encodeURIComponent(threshold)}`;
  }
  const response = await fetchWithAuth(url);
  await assertOk(response);
  return response.json();
};

export default fetchWithAuth;

export async function registerUserAccount(name: string, email: string, password: string): Promise<AuthResponse> {
  const url = `${apiEndpoint}/auth/signup`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ name, email, password });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Registration error response:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.detail || 'Registration failed');
      } catch (parseError) {
        throw new Error(`Registration failed: ${errorText}`);
      }
    }
    return response.json();
  } catch (error) {
    console.error('Error user account register:', error);
    throw error;
  }
}

export async function loginUserAccount(email: string, password: string): Promise<AuthResponse> {
  const url = `${apiEndpoint}/auth/signin`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ email, password });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Login error response:', errorText);
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.detail || 'Login failed');
      } catch (parseError) {
        throw new Error(`Login failed: ${errorText}`);
      }
    }
    return response.json();
  } catch (error) {
    console.error('Error fetching auth token:', error);
    throw error;
  }
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  const response = await fetch(`${apiEndpoint}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Forgot password request failed: ${errorText}`);
  }
  return response.json();
}

export async function verifyPasswordResetToken(token: string): Promise<{ valid: boolean }> {
  const response = await fetch(
    `${apiEndpoint}/auth/verify-reset-token/${encodeURIComponent(token)}`,
  );
  if (!response.ok) {
    throw new Error('Failed to verify reset token');
  }
  return response.json();
}

export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  const response = await fetch(`${apiEndpoint}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.detail || 'Password reset failed');
    } catch {
      throw new Error(`Password reset failed: ${errorText}`);
    }
  }
  return response.json();
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  const response = await fetchWithAuth(`${apiEndpoint}/user/password-change`, {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.detail || 'Password change failed');
    } catch {
      throw new Error(`Password change failed: ${errorText}`);
    }
  }
  return response.json();
}

export async function createCampaign(campaign: CampaignCreate) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${campaign.organization_id}/campaigns`, {
    method: 'POST',
    body: JSON.stringify(campaign),
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const data = await response.json();
  // Handle both the old response format (full campaign object) and
  // the new Celery task response format (message and campaign_id)
  if (data.campaign_id) {
    // Return an object that matches the expected structure in components
    return {
      id: data.campaign_id,
      message: data.message,
      slug: campaign.slug,
      website_url: campaign.website_url,
      // Include other fields to satisfy type requirements
      state: 'DRAFT',
      is_celery_task: true,
    };
  }
  return data;
}

export async function createOnboardingCampaign(campaign: CampaignCreate) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/organizations/${campaign.organization_id}/campaigns/onboarding`,
    {
      method: 'POST',
      body: JSON.stringify(campaign),
    },
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const data = await response.json();
  // Handle the Celery task response format (message and campaign_id)
  if (data.campaign_id) {
    // Return an object that matches the expected structure in components
    return {
      id: data.campaign_id,
      message: data.message,
      slug: campaign.slug,
      website_url: campaign.website_url,
      // Include other fields to satisfy type requirements
      state: 'DRAFT',
      is_celery_task: true,
    };
  }
  return data;
}

export async function createBrandCampaign(campaign: CampaignCreate) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${campaign.organization_id}/campaigns/brand`, {
    method: 'POST',
    body: JSON.stringify(campaign),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Network response was not ok');
  }
  const data = await response.json();
  // Handle the Celery task response format (message and campaign_id)
  if (data.campaign_id) {
    // Return an object that matches the expected structure in components
    return {
      id: data.campaign_id,
      message: data.message,
      slug: campaign.slug,
      website_url: campaign.website_url,
      // Include other fields to satisfy type requirements
      state: 'DRAFT',
      is_celery_task: true,
    };
  }
  return data;
}

export async function createCompetitorCampaign(
  campaign: CompetitorCampaignCreate,
  organizationId: number
): Promise<CompetitorCampaignResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/organizations/${organizationId}/campaigns/competitors`,
    {
      method: 'POST',
      body: JSON.stringify(campaign),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create competitor campaign' }));
    const errorMessage = typeof error.detail === 'string'
      ? error.detail
      : JSON.stringify(error.detail) || 'Failed to create competitor campaign';
    throw new Error(errorMessage);
  }
  return response.json();
}

/**
 * Create a HiPo / ICP / Location campaign via the unified optimisation
 * endpoint. The campaign is created asynchronously by the Celery task; this
 * call returns 202 with a thin acknowledgment. Frontend polls /campaigns or
 * task_tracking to discover the resulting campaign.
 *
 * For ICP fan-out (one POST per chosen ICP), call this once per ICP and
 * collect the responses.
 */
export async function createOptimisationCampaign(
  payload: OptimisationCampaignCreatePayload,
  organizationId: number,
  idempotencyKey?: string
): Promise<OptimisationCampaignCreateAccepted> {
  const headers: Record<string, string> = {};
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }
  const response = await fetchWithAuth(
    `${apiEndpoint}/organizations/${organizationId}/campaigns/optimisation`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers,
    }
  );
  if (!response.ok) {
    // 409 only fires when the same Idempotency-Key is in flight twice — almost
    // certainly a double-click or a retry. The raw "Duplicate request in
    // progress" detail is technically accurate but reads like an error to the
    // user, so soften it.
    if (response.status === 409) {
      throw new Error(
        'Looks like that submission is already in flight. Check your campaigns list in a moment, or wait and try again.'
      );
    }
    const error = await response.json().catch(() => ({ detail: 'Failed to create optimisation campaign' }));
    const errorMessage = typeof error.detail === 'string'
      ? error.detail
      : JSON.stringify(error.detail) || 'Failed to create optimisation campaign';
    throw new Error(errorMessage);
  }
  return response.json();
}

/**
 * Fetch the source campaign's keywords with their (zero-defaulted)
 * performance metrics for the HiPo wizard's keyword multi-select step.
 * The backend orders by avg_monthly_searches DESC NULLS LAST, then id ASC,
 * so the highest-volume keywords float to the top while the response stays
 * deterministic for DRAFT campaigns that have no metrics yet.
 */
export async function getCampaignKeywordsWithPerf(
  campaignId: number
): Promise<CampaignKeywordWithPerf[]> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/keywords-with-perf`
  );
  if (!response.ok) {
    throw new Error('Failed to fetch campaign keywords with performance');
  }
  return response.json();
}

export async function addCompetitorsToCampaign(
  campaignId: number,
  competitorIds: string[]
): Promise<AddCompetitorsToCampaignResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/competitors`,
    {
      method: 'POST',
      body: JSON.stringify({ competitor_ids: competitorIds }),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to add competitors to campaign' }));
    const errorMessage = typeof error.detail === 'string'
      ? error.detail
      : JSON.stringify(error.detail) || 'Failed to add competitors to campaign';
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function getBusinessByOrganization(organizationId: number): Promise<Business | null> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/business`);
  if (!response.ok) {
    throw new Error('Failed to fetch business');
  }
  return response.json();
}

export async function fetchBusinessTaskTracking(businessId: number): Promise<BusinessTaskTrackingResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}/task-tracking`);
  if (!response.ok) {
    throw new Error('Failed to fetch business task tracking');
  }
  return response.json();
}

export interface LandingPageDetails {
  url: string;
  title: string | null;
  description: string | null;
  intent: string | null;
  ad_count: number;
  sitelink_count: number;
  product_count: number;
}

export async function getLandingPagesDetails(businessId: number): Promise<LandingPageDetails[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}/landing-pages/details`);
  if (!response.ok) {
    throw new Error('Failed to fetch landing pages details');
  }
  return response.json();
}

export async function addLandingPage(businessId: number, url: string): Promise<{ onboarding_links: string[] }> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/landing-pages`,
    { method: 'POST', body: JSON.stringify({ url }) },
  );
  if (!response.ok) {
    throw new Error('Failed to add landing page');
  }
  return response.json();
}

export async function deleteLandingPage(businessId: number, url: string): Promise<{ onboarding_links: string[] }> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/landing-pages?url=${encodeURIComponent(url)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error('Failed to delete landing page');
  }
  return response.json();
}

export async function createBusiness(business: BusinessCreate, organizationId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/businesses`, {
    method: 'POST',
    body: JSON.stringify(business),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const detail = errorData?.detail || `Business creation failed (${response.status})`;
    throw new Error(detail);
  }
  return response.json();
}

export async function updateBusinessDescription({
  description,
  businessUrl,
}: {
  description: string;
  businessUrl: string;
}) {
  const response = await fetchWithAuth(`${apiEndpoint}/business/description?url=${businessUrl}`, {
    method: 'PUT',
    body: JSON.stringify({ description }),
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export type BusinessUpdateData = {
  business_url?: string;
  business_desc?: string;
  brand_name?: string;
  industry?: { value: string; label: string; tier: string } | null;
  trust_signals?: string[] | null;
  brand_identity?: {
    archetypes: { primary: string; secondary?: string };
    voice: { formality: number; humor: number; respect: number; enthusiasm: number; technicality: number };
    differentiators: string[];
  } | null;
  icps?: Array<{
    id: string;
    name: string;
    title: string;
    market_types: string[];
    firmographics: { company_size: string; industry: string; revenue: string };
    demographics: { age_range: string; location: string; job_title: string };
    pgob: { pains: string[]; gains: string[]; objections: string[]; behaviors: string[] };
  }> | null;
  products_data?: Array<{
    id: string;
    name: string;
    description: string;
    market_types: string[];
    features?: string[];
    landing_page_url?: string | null;
  }> | null;
  product_icp_links?: Array<{ product_id: string; icp_id: string }> | null;
  settings?: {
    sells_services?: boolean;
    brand_adherence_mode?: string;
    llm_instructions?: string;
  } | null;
  platform_budgets?: Array<{ platform: string; amount: number }> | null;
  target_locations?: Array<{ criteria_id: number; canonical_name?: string }> | null;
};

export async function updateBusiness(businessId: number, data: BusinessUpdateData): Promise<Business> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to update business');
  }
  return response.json();
}

export async function generateBrandIntel(businessId: number): Promise<{ task_tracking_id: number; business_id: number }> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}/generate-brand-intel`, {
    method: 'POST',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const detail = errorData?.detail || `Failed to trigger brand intel generation (${response.status})`;
    throw new Error(detail);
  }
  return response.json();
}

// AI-generate ONE new product (#5378) / customer persona (#5377) from the business's existing context
// plus an optional free-text hint. Synchronous: the backend returns the fully-formed, appended item.
export async function generateProduct(businessId: number, context?: string): Promise<import('./types').ProductData> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}/products/generate`, {
    method: 'POST',
    body: JSON.stringify({ context: context || null }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const detail = errorData?.detail || `Failed to generate product (${response.status})`;
    throw new Error(detail);
  }
  return response.json();
}

export async function generatePersona(businessId: number, context?: string): Promise<import('./types').Icp> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}/personas/generate`, {
    method: 'POST',
    body: JSON.stringify({ context: context || null }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const detail = errorData?.detail || `Failed to generate customer type (${response.status})`;
    throw new Error(detail);
  }
  return response.json();
}

export async function fetchLocations(query: string): Promise<LocationType[] | undefined> {
  if (query.trim().length < 3) return [];

  try {
    const response = await fetchWithAuth(`${apiEndpoint}/locations?name=${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data: LocationType[] = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error fetching items:', error);
    return [];
  }
}

/**
 * Resolve a place name (a geo-target's canonical name) to its boundary polygon
 * (GeoJSON geometry) for map highlighting. Returns null when no boundary exists
 * — Google Ads geo-targets stay the source of truth; this is just the overlay.
 */
export async function fetchLocationBoundary(
  businessId: number,
  query: string,
  criteriaId?: number,
): Promise<import('geojson').Geometry | null> {
  if (!query.trim()) return null;
  try {
    // Pass the geo-target's criteria_id so the backend persists the resolved
    // boundary (keyed by criteria_id) — instant on every later explore (#5359).
    const params = new URLSearchParams({ q: query });
    if (criteriaId != null) params.set('criteria_id', String(criteriaId));
    const response = await fetchWithAuth(
      `${apiEndpoint}/business/${businessId}/locations/boundary?${params.toString()}`,
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data?.geometry ?? null;
  } catch {
    return null;
  }
}

// The /locations/by-ids endpoint caps each request at 200 ids (see
// campaign/router.py). A business can target far more than that (e.g. a
// whole-country catalog of 200+ regions), so split into ≤200-id batches.
const LOCATIONS_BY_IDS_BATCH_SIZE = 200;

export async function fetchLocationsByIds(criteriaIds: number[]): Promise<LocationType[]> {
  // Server-side lookup against the cached Location table. Used by the location
  // tables to render canonical_name + reach for rows whose location_id wouldn't
  // appear in the broad locationsQueryOptions search seed. Reach missing from the
  // cache is backfilled once from the live geo search server-side, then cached
  // (#5481), so this is cheap in steady state.
  if (criteriaIds.length === 0) return [];

  const batches: number[][] = [];
  for (let i = 0; i < criteriaIds.length; i += LOCATIONS_BY_IDS_BATCH_SIZE) {
    batches.push(criteriaIds.slice(i, i + LOCATIONS_BY_IDS_BATCH_SIZE));
  }

  const results = await Promise.all(
    batches.map(async (batch) => {
      const response = await fetchWithAuth(
        `${apiEndpoint}/locations/by-ids?ids=${batch.join(',')}`,
      );
      if (!response.ok) throw new Error('Failed to look up locations by id');
      return response.json() as Promise<LocationType[]>;
    }),
  );

  return results.flat();
}

export async function fetchCountries(): Promise<LocationType[]> {
  // All countries from the cached Location catalog, A–Z. Local read, no Google
  // Ads call. Backs the browsable country picker on the Add-location dialog (#4497).
  const response = await fetchWithAuth(`${apiEndpoint}/locations/countries`);
  if (!response.ok) throw new Error('Failed to load countries');
  return response.json();
}

export async function fetchCountryLocations(params: {
  countryCode: string;
  parentId?: number | null;
  q?: string;
}): Promise<LocationType[]> {
  // Sub-locations within one country (#4497). With `q`: name search across the
  // country. Without `q`: the direct children of `parentId` (one level down).
  const search = new URLSearchParams({ country_code: params.countryCode });
  if (params.q && params.q.trim()) search.set('q', params.q.trim());
  if (params.parentId != null) search.set('parent_id', String(params.parentId));
  const response = await fetchWithAuth(`${apiEndpoint}/locations/by-country?${search.toString()}`);
  if (!response.ok) throw new Error('Failed to load locations for country');
  return response.json();
}

export async function fetchNearByLocations(location: LocationType | null): Promise<LocationType[] | undefined> {
  if (!location) return [];

  try {
    const response = await fetchWithAuth(`${apiEndpoint}/locations/nearby`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data: LocationType[] = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error fetching items:', error);
    return [];
  }
}

export interface CampaignsWithMeta {
  campaigns: CampaignData[];
  meta: Record<string, unknown> | null;
}

// Full payload incl. response meta — query options cache this and project the
// array out via select; meta is read as a second observer (#4884, replaces
// the window.__campaignsMeta side-channel).
export async function fetchCampaignsWithMeta(
  orgId?: number | null,
  opts: { includeExternal?: boolean; includeArchived?: boolean; cache?: boolean } = {},
): Promise<CampaignsWithMeta> {
  const { includeExternal = false, includeArchived = false, cache = true } = opts;
  const params = new URLSearchParams();
  if (includeExternal) params.set('include_external', 'true');
  if (includeArchived) params.set('include_archived', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${orgId}/campaigns${qs}`, {
    method: 'GET',
    headers: cache
      ? {}
      : {
          'Cache-Control': 'no-cache',
        },
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  const data = await response.json();

  return { campaigns: data.campaigns, meta: data.meta ?? null };
}

export async function fetchCampaigns(
  orgId?: number | null,
  opts: { includeExternal?: boolean; includeArchived?: boolean; cache?: boolean } = {},
): Promise<CampaignData[]> {
  return (await fetchCampaignsWithMeta(orgId, opts)).campaigns;
}

export async function fetchGoogleCampaigns(): Promise<GoogleCampaign[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/campaigns`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

// Error carrying the HTTP status so callers can distinguish a genuine 404 (campaign deleted /
// bad slug) from server errors — the campaign route loader only redirects on 404 (#5001).
export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

export async function fetchCampaign(slug: string): Promise<CampaignData> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${slug}`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new HttpError(response.status, `Request failed: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return data;
}

export async function fetchCampaignRecommendations(campaignId: number): Promise<CampaignRecommendations | null> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/recommendations`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function updateCampaignRecommendationFix(campaignId: number, campaignRecommendationId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/recommendations/${campaignRecommendationId}/apply-instant-fix`,
    {
      method: 'POST',
    },
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function updateAdRecommendationFix(campaignId: number, adRecommendationId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ad-recommendation/${adRecommendationId}/apply-instant-fix`,
    {
      method: 'POST',
    },
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function updateKeywordRecommendationFix(campaignId: number, keywordRecommendationId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/keyword-recommendation/${keywordRecommendationId}/apply-instant-fix`,
    {
      method: 'POST',
    },
  );
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function updateAdState(campaignId: number, adId: number, state: 'approved' | 'draft' | 'error') {
  // Convert state to uppercase to match backend enum
  const upperState = state.toUpperCase();
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/state`, {
    method: 'PUT',
    body: JSON.stringify({ state: upperState }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function updateCampaignState(
  campaignId: number,
  state: CAMPAIGN_STATE,
): Promise<CampaignStateUpdateAccepted> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/state`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ state }),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({ detail: 'Failed to update state' }));
    throw new Error(detail.detail ?? 'Failed to update state');
  }
  return response.json();
}

export async function triggerReverseSync(campaignId: number): Promise<CampaignStateUpdateAccepted> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/reverse-sync`, {
    method: 'POST',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    if (response.status === 429) {
      throw new Error(body.detail ?? 'Synced recently. Try again in a few minutes.');
    }
    throw new Error(body.detail ?? 'Failed to start sync');
  }
  return response.json();
}

export async function getCampaignExternalChanges(campaignId: number): Promise<{ has_external_changes: boolean }> {
  // Cheap on-open drift probe (#5566 Req 1). Best-effort: a transient failure must never block the
  // page load or trigger a pull, so fail closed-to-false on any non-ok response.
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/external-changes`);
  if (!response.ok) {
    return { has_external_changes: false };
  }
  return response.json();
}

export async function updateGeoTargetingMode(
  campaignId: number,
  body: { positive_geo_target_type?: LocationTargetingMode; negative_geo_target_type?: LocationTargetingMode },
): Promise<GeoTargetingModeResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/geo-targeting-mode`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({ detail: 'Failed to update location targeting mode' }));
    throw new Error(detail.detail ?? 'Failed to update location targeting mode');
  }
  return response.json();
}

export async function importGoogleAdsCampaign(gaCampaignId: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/google/campaigns/${gaCampaignId}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  return response.json();
}

export async function deleteCampaign(campaignId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to delete campaign');
  }
  if (response.status !== 204) {
    return await response.json();
  }

  return null;
}

export async function deleteCampaignErrors(campaignId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/errors`, {
    method: 'Delete',
  });
  if (!response.ok) {
    throw new Error('Failed to delete campaign');
  }
  if (response.status !== 204) {
    return await response.json();
  }

  return null;
}

export async function updateCampaign(campaign: CampaignUpdate) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaign.id}`, {
    method: 'PUT',
    body: JSON.stringify(campaign),
  });
  if (!response.ok) {
    if (response.status === 409) {
      const body = await response.json().catch(() => null);
      // Revoked Google connection (#5935) — distinct from drift: only re-auth fixes it, so it
      // must not fall through to CampaignDriftError and wrongly open the drift modal.
      if (body?.detail?.code === 'google_ads_connection_broken') {
        throw new GoogleAdsConnectionBrokenError(body.detail.message);
      }
      throw new CampaignDriftError(body?.detail ?? 'Changed in Google Ads', body?.changes ?? []);
    }
    // Surface the server detail on 5xx too (e.g. a Google Ads sync 502) instead of a generic
    // message the user can't act on (#5755).
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to update campaign. Please try again.');
  }
  return response.json();
}

/** Set a campaign's radius (proximity) targets via the thin endpoint (#5444) — used
 * by the creation wizard to apply the chosen radii right after create. `[]` clears
 * them; a non-empty list customizes the campaign with those radii. */
export async function setCampaignProximityTargets(campaignId: number, targets: CampaignProximityInput[]) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/proximity-targets`, {
    method: 'PUT',
    body: JSON.stringify(targets),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to set campaign radius targets');
  }
  return response.json();
}

export async function validateCampaignAssets(campaignId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/validate`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  return response.json();
}

export async function updateCampaignBudget(campaignId: number, budgetUpdate: BudgetUpdate) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/budget`, {
    method: 'PUT',
    body: JSON.stringify(budgetUpdate),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    const detail = errorRes.detail;
    throw new Error(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((e: { msg: string }) => e.msg).join(', ') : 'Failed to update budget');
  }
  return response.json();
}

// Bid Adjustment Types
export interface DeviceBidModifierRequest {
  device_type: 'MOBILE' | 'DESKTOP' | 'TABLET';
  modifier_value: number; // -0.9 to 9.0 (-90% to +900%)
}

export interface LocationBidModifierRequest {
  location_criteria_id: string;
  modifier_value: number; // -0.9 to 9.0 (-90% to +900%)
}

export interface ScheduleBidModifierRequest {
  day_of_week: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  start_hour: number; // 0-23
  end_hour: number; // 0-23
  modifier_value: number; // -0.9 to 9.0 (-90% to +900%)
}

export interface BidModifierWarning {
  code: string;
  message: string;
}

export interface BidModifierResponse {
  message: string;
  resource_name?: string;
  criterion_id?: string;
  info_messages?: string[];
  warnings?: BidModifierWarning[];
}

export interface ProximityBidModifierRow {
  id: number;
  criterion_id: string;
  modifier_value: number;
  latitude: number | null;
  longitude: number | null;
  radius_km: number | null;
  radius_address: string | null;
}

export interface ProximityBidModifierResponse extends BidModifierResponse {
  id?: number;
  latitude?: number;
  longitude?: number;
  radius_km?: number;
  radius_address?: string;
  modifier_value?: number;
}

export interface ProximityBidModifierCreateRequest {
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_km: number; // 1..80
  modifier_value: number; // -0.9..9.0 (offset, e.g. 0.2 for +20%)
}

export interface ProximityBidModifierUpdateRequest {
  modifier_value: number;
}

export interface BidModifiersResponse {
  device: Array<{
    criterion_id: string;
    device_type: string;
    modifier_value: number;
  }>;
  location: Array<{
    criterion_id: string;
    location_criteria_id: string;
    modifier_value: number;
  }>;
  schedule: Array<{
    criterion_id: string;
    day_of_week: string;
    start_hour: number;
    end_hour: number;
    modifier_value: number;
  }>;
  proximity?: Array<{
    criterion_id: string;
    modifier_value: number;
  }>;
}

export async function setDeviceBidModifier(
  campaignId: number,
  request: DeviceBidModifierRequest
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/device`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to set device bid modifier');
  }
  return response.json();
}

export async function setLocationBidModifier(
  campaignId: number,
  request: LocationBidModifierRequest
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/location`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to set location bid modifier');
  }
  return response.json();
}

export async function setScheduleBidModifier(
  campaignId: number,
  request: ScheduleBidModifierRequest
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/schedule`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to set schedule bid modifier');
  }
  return response.json();
}

export async function getBidModifiers(campaignId: number): Promise<BidModifiersResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/bid-adjustments`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get bid modifiers');
  }
  return response.json();
}

export async function deleteDeviceBidModifier(
  campaignId: number,
  criterionId: string
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/device/${criterionId}`,
    {
      method: 'DELETE',
    }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete device bid modifier');
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Proximity bid modifiers (#4384)
// ---------------------------------------------------------------------------

export async function listProximityBidModifiers(
  campaignId: number
): Promise<ProximityBidModifierRow[]> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/proximity`,
    { method: 'GET' }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to list proximity bid modifiers');
  }
  return response.json();
}

export async function createProximityBidModifier(
  campaignId: number,
  request: ProximityBidModifierCreateRequest
): Promise<ProximityBidModifierResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/proximity`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to create proximity bid modifier');
  }
  return response.json();
}

export async function updateProximityBidModifier(
  campaignId: number,
  modifierId: number,
  request: ProximityBidModifierUpdateRequest
): Promise<ProximityBidModifierResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/proximity/${modifierId}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to update proximity bid modifier');
  }
  return response.json();
}

export async function deleteProximityBidModifier(
  campaignId: number,
  modifierId: number
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/proximity/${modifierId}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete proximity bid modifier');
  }
  return response.json();
}

export async function deleteLocationBidModifier(
  campaignId: number,
  criterionId: string
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/location/${criterionId}`,
    {
      method: 'DELETE',
    }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete location bid modifier');
  }
  return response.json();
}

export async function deleteScheduleBidModifier(
  campaignId: number,
  criterionId: string
): Promise<BidModifierResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/bid-adjustments/schedule/${criterionId}`,
    {
      method: 'DELETE',
    }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete schedule bid modifier');
  }
  return response.json();
}

// Ad Schedule (Day Parting) Types
export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface AdScheduleCreate {
  day_of_week: DayOfWeek;
  start_hour: number; // 0-23
  start_minute: number; // 0, 15, 30, 45
  end_hour: number; // 0-24 (24 means end of day)
  end_minute: number; // 0, 15, 30, 45
  bid_modifier?: number; // 0.1 to 10.0 (optional)
}

export interface AdScheduleUpdate {
  day_of_week?: DayOfWeek;
  start_hour?: number;
  start_minute?: number;
  end_hour?: number;
  end_minute?: number;
  bid_modifier?: number | null; // null to remove modifier
}

export interface AdScheduleResponse {
  id: number;
  campaign_id: number;
  day_of_week: string;
  start_hour: number;
  start_minute: number;
  end_hour: number;
  end_minute: number;
  bid_modifier?: number | null;
  googleads_criterion_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdSchedulesResponse {
  schedules: AdScheduleResponse[];
}

// Ad Schedule API Functions
export async function createAdSchedule(
  campaignId: number,
  schedule: AdScheduleCreate
): Promise<AdScheduleResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ad-schedule`, {
    method: 'POST',
    body: JSON.stringify(schedule),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to create ad schedule');
  }
  return response.json();
}

export async function getAdSchedules(campaignId: number): Promise<AdSchedulesResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ad-schedule`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get ad schedules');
  }
  return response.json();
}

// Tracking Template / UTM Parameters (#4379, read-only)

export interface UtmParameter {
  key: string;
  value: string;
}

export interface CampaignTrackingTemplateResponse {
  campaign_id: number;
  googleads_campaign_id: string | null;
  is_published: boolean;
  tracking_url_template: string | null;
  final_url_suffix: string | null;
  utm_parameters: UtmParameter[];
}

export async function getCampaignTrackingTemplate(campaignId: number): Promise<CampaignTrackingTemplateResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/tracking-template`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get tracking template');
  }
  return response.json();
}

export async function updateAdSchedule(
  campaignId: number,
  scheduleId: number,
  schedule: AdScheduleUpdate
): Promise<AdScheduleResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ad-schedule/${scheduleId}`, {
    method: 'PUT',
    body: JSON.stringify(schedule),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to update ad schedule');
  }
  return response.json();
}

export async function deleteAdSchedule(campaignId: number, scheduleId: number): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ad-schedule/${scheduleId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete ad schedule');
  }
}

// Campaign Negative Keywords Types
export type NegativeKeywordMatchType = 'EXACT' | 'PHRASE' | 'BROAD';

export interface CampaignNegativeKeywordCreate {
  keyword: string;
  match_type: NegativeKeywordMatchType;
}

export interface CampaignNegativeKeywordResponse {
  id: number;
  campaign_id: number;
  keyword: string;
  match_type: string;
  is_paused: boolean;
  created_at: string;
  updated_at: string;
}

// A negative inherited from a higher level (shared list / campaign), surfaced read-only.
// source: 'shared_list' | 'campaign'; source_label is the list name or 'Campaign'.
export interface InheritedNegativeKeyword {
  keyword: string;
  match_type: string;
  source: 'shared_list' | 'campaign';
  source_label: string;
}

export interface CampaignNegativeKeywordsResponse {
  negative_keywords: CampaignNegativeKeywordResponse[];
  inherited_negatives?: InheritedNegativeKeyword[];
}

export interface CampaignNegativeKeywordsCreate {
  keywords: CampaignNegativeKeywordCreate[];
}

// Ad-level Negative Keywords API
export interface AdNegativeKeywordResponse {
  id: number;
  keyword: string;
  match_type: string;
  is_paused: boolean;
  created_at: string | null;
  updated_at: string | null;
  ad_id: number;
  googleads_ad_group_id: string | null;
}

export interface AdNegativeKeywordsResponse {
  negative_keywords: AdNegativeKeywordResponse[];
  inherited_negatives?: InheritedNegativeKeyword[];
}

export async function getAdNegativeKeywords(
  campaignId: number,
  adId: number,
  includePaused: boolean = false
): Promise<AdNegativeKeywordsResponse> {
  const params = includePaused ? '?include_paused=true' : '';
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/negative-keywords${params}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get ad negative keywords');
  }
  return response.json();
}

// Campaign Negative Keywords API Functions
export async function getCampaignNegativeKeywords(
  campaignId: number,
  includePaused: boolean = false
): Promise<CampaignNegativeKeywordsResponse> {
  const params = includePaused ? '?include_paused=true' : '';
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/negative-keywords${params}`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get campaign negative keywords');
  }
  return response.json();
}

export async function addCampaignNegativeKeywords(
  campaignId: number,
  keywords: CampaignNegativeKeywordsCreate
): Promise<CampaignNegativeKeywordsResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/negative-keywords`, {
    method: 'POST',
    body: JSON.stringify(keywords),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to add campaign negative keywords');
  }
  return response.json();
}

export async function deleteCampaignNegativeKeyword(
  campaignId: number,
  keywordId: number
): Promise<void> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/negative-keywords/${keywordId}`,
    {
      method: 'DELETE',
    }
  );
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete campaign negative keyword');
  }
}

// Campaign-level IP Exclusions API
export interface CampaignIpExclusion {
  id: number;
  campaign_id: number;
  ip_address: string;
  created_at: string;
  updated_at: string;
  googleads_criterion_id?: string | null;
}

export interface CampaignIpExclusionsResponse {
  ip_exclusions: CampaignIpExclusion[];
  count: number;
}

export async function getCampaignIpExclusions(campaignId: number): Promise<CampaignIpExclusionsResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ip-exclusions`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get campaign IP exclusions');
  }
  return response.json();
}

export async function addCampaignIpExclusions(
  campaignId: number,
  ipAddresses: string[]
): Promise<CampaignIpExclusionsResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ip-exclusions`, {
    method: 'POST',
    body: JSON.stringify({ ip_addresses: ipAddresses }),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to add campaign IP exclusions');
  }
  return response.json();
}

export async function deleteCampaignIpExclusion(campaignId: number, exclusionId: number): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ip-exclusions/${exclusionId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete campaign IP exclusion');
  }
}

export async function updateCampaignAllStateApprove(campaignSlug: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignSlug}/state/all-approved`, {
    method: 'PUT',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to approve campaign assets');
  }
  return response.json();
}

export async function getBudgetRecommendation(campaignId: number, signal?: AbortSignal): Promise<{ budget: number }> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/budget_recommendation`, {
    method: 'GET',
    signal,
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch budget recommendation: ${errorText}`);
  }
  return response.json();
}

export async function getAccessTokenFromGoogleAccessToken({ googleAccessToken }: { googleAccessToken: string }) {
  const url = `${apiEndpoint}/auth/google/token`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ google_access_token: googleAccessToken });

  const response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes?.detail || 'An unexpected error occurred');
  }

  const data = await response.json();
  return data;
}

export async function getAccessTokenFromGoogleAccessTokenWithInviteCode({
  googleAccessToken,
  inviteCode,
}: {
  googleAccessToken: string;
  inviteCode: string;
}) {
  const url = `${apiEndpoint}/auth/google/token/invite/${inviteCode}`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ google_access_token: googleAccessToken });

  const response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes?.detail || 'Network response was not ok');
  }

  const data = await response.json();
  return data;
}

export async function getAuthToken({ authCode }: { authCode: string }) {
  const url = `${apiEndpoint}/google/auth`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ code: authCode });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: body,
    });

    if (!response.ok) {
      const errorRes = await response.json().catch(() => null);
      throw new Error(errorRes?.detail || `HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Error fetching auth token:', error);
    throw error;
  }
}

export async function getAuthTokenWithInviteCode({ authCode, inviteCode }: { authCode: string; inviteCode: string }) {
  const url = `${apiEndpoint}/google/auth/invite/${inviteCode}`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ code: authCode });

  const response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  const data = await response.json();
  return data.access_token;
}

export async function addOrUpdateSiteLink(campaignId: number, siteLink: SiteLink) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/site-links`, {
    method: 'POST',
    body: JSON.stringify(siteLink),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save sitelink' }));
    throw new Error(error.detail || 'Failed to save sitelink');
  }
  return response.json();
}

export async function deleteSiteLink(campaignId: number, siteLinkId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/site-links/${siteLinkId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete sitelink' }));
    throw new Error(error.detail || 'Failed to delete sitelink');
  }
  return response.json();
}

export async function addOrUpdateCallout(campaignId: number, callout: CalloutType) {
  const url = `${apiEndpoint}/campaigns/${campaignId}/callouts`;
  const method = 'POST';

  const response = await fetchWithAuth(url, {
    method,
    body: JSON.stringify(callout),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save callout' }));
    throw new Error(error.detail || 'Failed to save callout');
  }

  return response.json();
}

export async function deleteCallout(campaignId: number, calloutId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/callouts/${calloutId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete callout' }));
    throw new Error(error.detail || 'Failed to delete callout');
  }

  return response.json();
}

export type PhoneNumberPayload = {
  id?: number;
  value: string;
  country_code?: string | null;
  call_extension_enabled: boolean;
};

export async function savePhoneNumber(campaignId: number, phoneNumber: PhoneNumberPayload) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/phone-numbers`, {
    method: 'POST',
    body: JSON.stringify(phoneNumber),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save phone number' }));
    throw new Error(error.detail || 'Failed to save phone number');
  }
  return response.json();
}

export async function deletePhoneNumber(campaignId: number, phoneNumberId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/phone-numbers/${phoneNumberId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete phone number' }));
    throw new Error(error.detail || 'Failed to delete phone number');
  }
  return response.json();
}

// Image extensions (#4376): attach/detach a creative-asset image to a campaign (AD_IMAGE)
export async function attachCampaignImageExtension(campaignId: number, creativeAssetId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/image-extensions/${creativeAssetId}`,
    { method: 'POST' },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to add image extension' }));
    throw new Error(error.detail || 'Failed to add image extension');
  }

  return response.json();
}

export async function detachCampaignImageExtension(campaignId: number, creativeAssetId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/image-extensions/${creativeAssetId}`,
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to remove image extension' }));
    throw new Error(error.detail || 'Failed to remove image extension');
  }

  return response.json();
}

// Account-level callout functions
export async function createAccountCallout(calloutText: string): Promise<AccountCallout> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/callouts`, {
    method: 'POST',
    body: JSON.stringify({ callout_text: calloutText }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network response was not ok' }));
    throw new Error(error.detail || 'Failed to create account callout');
  }

  return response.json();
}

export async function listAccountCallouts(): Promise<AccountCalloutListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/callouts`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export async function deleteAccountCallout(customerAssetResourceName: string): Promise<void> {
  const encodedResourceName = encodeURIComponent(customerAssetResourceName);
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/callouts?customer_asset_resource_name=${encodedResourceName}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network response was not ok' }));
    throw new Error(error.detail || 'Failed to delete account callout');
  }

  return response.json();
}

export async function updateAccountCallout(
  customerAssetResourceName: string,
  calloutText: string,
): Promise<AccountCallout> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/callouts`, {
    method: 'PUT',
    body: JSON.stringify({
      customer_asset_resource_name: customerAssetResourceName,
      callout_text: calloutText,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network response was not ok' }));
    throw new Error(error.detail || 'Failed to update account callout');
  }

  return response.json();
}

// Account Sitelinks

export async function listAccountSitelinks(): Promise<AccountSitelinkListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/sitelinks`);
  if (!response.ok) throw new Error('Failed to fetch account sitelinks');
  return response.json();
}

export async function createAccountSitelink(data: {
  name: string;
  url: string;
  description1: string;
  description2: string;
}): Promise<AccountSitelink> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/sitelinks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error('Failed to create account sitelink');
  return response.json();
}

export async function updateAccountSitelink(
  uuid: string,
  data: { name: string; url: string; description1: string; description2: string },
): Promise<AccountSitelink> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/sitelinks`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uuid, ...data }),
  });
  if (!response.ok) throw new Error('Failed to update account sitelink');
  return response.json();
}

export async function deleteAccountSitelink(uuid: string): Promise<void> {
  const params = new URLSearchParams({ uuid });
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/sitelinks?${params}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete account sitelink');
}

// Shared Negative Keyword Lists (Google Ads SharedSet of type NEGATIVE_KEYWORDS)

const negativeListsBase = `${apiEndpoint}/google/account/negative-keyword-lists`;

export async function listNegativeKeywordLists(): Promise<NegativeKeywordListsResponse> {
  const response = await fetchWithAuth(negativeListsBase);
  if (!response.ok) throw new Error('Failed to fetch negative keyword lists');
  return response.json();
}

export async function createNegativeKeywordList(name: string): Promise<NegativeKeywordList> {
  const response = await fetchWithAuth(negativeListsBase, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || 'Failed to create list');
  return response.json();
}

export async function renameNegativeKeywordList(listId: string, name: string): Promise<NegativeKeywordList> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || 'Failed to rename list');
  return response.json();
}

export async function deleteNegativeKeywordList(listId: string): Promise<void> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to delete list');
}

export async function listNegativeKeywordListKeywords(listId: string): Promise<SharedNegativeKeywordsResponse> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}/keywords`);
  if (!response.ok) throw new Error('Failed to fetch list keywords');
  return response.json();
}

export async function addNegativeKeywordListKeywords(
  listId: string,
  keywords: string[],
  matchType: string,
): Promise<SharedNegativeKeywordsResponse> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}/keywords`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keywords, match_type: matchType }),
  });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || 'Failed to add keywords');
  return response.json();
}

export async function removeNegativeKeywordListKeyword(listId: string, resourceName: string): Promise<void> {
  const params = new URLSearchParams({ resource_name: resourceName });
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}/keywords?${params}`, { method: 'DELETE' });
  if (!response.ok) throw new Error('Failed to remove keyword');
}

export async function listNegativeKeywordListCampaigns(listId: string): Promise<AppliedCampaignsResponse> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}/campaigns`);
  if (!response.ok) throw new Error('Failed to fetch applied campaigns');
  return response.json();
}

export async function applyNegativeKeywordListToCampaigns(listId: string, campaignIds: number[]): Promise<void> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}/campaigns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ campaign_ids: campaignIds }),
  });
  if (!response.ok) throw new Error((await response.json().catch(() => null))?.detail || 'Failed to apply list');
}

export async function removeNegativeKeywordListFromCampaign(listId: string, campaignId: number): Promise<void> {
  const response = await fetchWithAuth(`${negativeListsBase}/${listId}/campaigns/${campaignId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to remove list from campaign');
}

// GA shared-set ids of the shared negative lists applied to a campaign (for the campaign-side
// "Apply List" modal to pre-check already-attached lists).
export async function getCampaignNegativeKeywordLists(campaignId: number): Promise<{ applied_shared_set_ids: string[] }> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/negative-keyword-lists`);
  if (!response.ok) throw new Error('Failed to fetch applied negative keyword lists');
  return response.json();
}

// Account-level IP Exclusions
export async function listIpExclusions(): Promise<IpExclusionListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/ip-exclusions`);
  if (!response.ok) throw new Error('Failed to fetch IP exclusions');
  return response.json();
}

export async function createIpExclusions(ipAddresses: string[]): Promise<IpExclusionListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/ip-exclusions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ip_addresses: ipAddresses }),
  });
  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to add IP exclusions');
  }
  return response.json();
}

export async function deleteIpExclusion(resourceName: string): Promise<void> {
  const params = new URLSearchParams({ resource_name: resourceName });
  const response = await fetchWithAuth(`${apiEndpoint}/google/account/ip-exclusions?${params}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to remove IP exclusion');
}

export async function getCampaignLanguages(campaignId: number): Promise<CampaignLanguageListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/languages`);
  if (!response.ok) throw new Error('Failed to fetch campaign languages');
  return response.json();
}

export async function addCampaignLanguage(campaignId: number, languageCode: string): Promise<CampaignLanguageListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/languages`, {
    method: 'POST',
    body: JSON.stringify({ language_code: languageCode }),
  });
  if (!response.ok) throw new Error('Failed to add language');
  return response.json();
}

export async function removeCampaignLanguage(campaignId: number, languageCode: string): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/languages/${languageCode}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to remove language');
}

export async function getAvailableLanguages(): Promise<CampaignLanguageListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/languages`);
  if (!response.ok) throw new Error('Failed to fetch available languages');
  return response.json();
}

export async function getExcludedLocations(campaignId: number): Promise<ExcludedLocationListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/excluded-locations`);
  if (!response.ok) throw new Error('Failed to fetch excluded locations');
  return response.json();
}

export async function addExcludedLocation(
  campaignId: number,
  criteriaId: number
): Promise<ExcludedLocationListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/excluded-locations`, {
    method: 'POST',
    body: JSON.stringify({ criteria_id: criteriaId }),
  });
  if (!response.ok) {
    const detail = await response.json().then((d) => d?.detail).catch(() => null);
    throw new Error(typeof detail === 'string' ? detail : 'Failed to add excluded location');
  }
  return response.json();
}

export async function removeExcludedLocation(campaignId: number, criteriaId: number): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/excluded-locations/${criteriaId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const detail = await response.json().then((d) => d?.detail).catch(() => null);
    throw new Error(typeof detail === 'string' ? detail : 'Failed to remove excluded location');
  }
}

export async function addOrUpdateStructuredSnippet(campaignId: number, structuredSnippet: StructuredSnippetType) {
  const url = `${apiEndpoint}/campaigns/${campaignId}/structured-snippets`;
  const method = structuredSnippet.id ? 'PUT' : 'POST';

  const response = await fetchWithAuth(`${url}${structuredSnippet.id ? `/${structuredSnippet.id}` : ''}`, {
    method,
    body: JSON.stringify(structuredSnippet),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save structured snippet' }));
    throw new Error(error.detail || 'Failed to save structured snippet');
  }

  return response.json();
}

export async function deleteStructuredSnippet(campaignId: number, structuredSnippetId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/structured-snippets/${structuredSnippetId}`,
    {
      method: 'DELETE',
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete structured snippet' }));
    throw new Error(error.detail || 'Failed to delete structured snippet');
  }

  return response.json();
}

export async function addOrUpdatePrice(campaignId: number, price: PriceType) {
  const url = `${apiEndpoint}/campaigns/${campaignId}/prices`;
  const method = price.id ? 'PUT' : 'POST';

  const response = await fetchWithAuth(`${url}${price.id ? `/${price.id}` : ''}`, {
    method,
    body: JSON.stringify(price),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save price' }));
    throw new Error(error.detail || 'Failed to save price');
  }

  return response.json();
}

export async function deletePrice(campaignId: number, priceId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/prices/${priceId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete price' }));
    throw new Error(error.detail || 'Failed to delete price');
  }

  return response.json();
}

// Backend: POST/PUT /campaigns/{id}/promotions, see app/campaign/router.py:3715+
export async function addOrUpdatePromotion(campaignId: number, promotion: PromotionType) {
  const url = `${apiEndpoint}/campaigns/${campaignId}/promotions`;
  const method = promotion.id ? 'PUT' : 'POST';

  const response = await fetchWithAuth(`${url}${promotion.id ? `/${promotion.id}` : ''}`, {
    method,
    body: JSON.stringify(promotion),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to save promotion' }));
    throw new Error(error.detail || 'Failed to save promotion');
  }

  return response.json();
}

export async function deletePromotion(campaignId: number, promotionId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/promotions/${promotionId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete promotion' }));
    throw new Error(error.detail || 'Failed to delete promotion');
  }

  return response.json();
}

export const updateAd = async (
  campaignId: number,
  adId: number,
  ad: AdUpdate,
  options: { signal?: AbortSignal } = {},
): Promise<Ad> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}`, {
    method: 'PUT',
    body: JSON.stringify(ad),
    signal: options.signal,
  });

  if (!response.ok) {
    if (response.status >= 500) throw new Error('Failed to save ad. Please try again.');
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to save ad');
  }

  return response.json();
};

export const renameAdGroup = async (
  campaignId: number,
  adId: number,
  name: string,
): Promise<{ success: boolean; ad_id: number; googleads_ad_group_name: string }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/ad-group-name`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });

  // assertOk throws a typed ApiError carrying the FastAPI `detail` (e.g. the friendly
  // duplicate-name message) as its message, which the mutation surfaces via toast.
  await assertOk(response, 'Failed to rename ad group');
  return response.json();
};

export const deleteAd = async (campaignId: number, adId: number): Promise<void> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete ad');
  }

  return response.json();
};

export const pauseAd = async (
  campaignId: number,
  adId: number
): Promise<{ message: string; ad_id: number; is_paused: boolean }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/pause`, {
    method: 'PUT',
  });

  if (!response.ok) {
    if (response.status >= 500) throw new Error('Failed to pause ad. Please try again.');
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to pause ad');
  }

  return response.json();
};

export const resumeAd = async (
  campaignId: number,
  adId: number
): Promise<{ message: string; ad_id: number; is_paused: boolean }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/resume`, {
    method: 'PUT',
  });

  if (!response.ok) {
    if (response.status >= 500) throw new Error('Failed to resume ad. Please try again.');
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to resume ad');
  }

  return response.json();
};

export const pauseFunnelStage = async (
  campaignId: number,
  adId: number,
  funnelStageId: number,
): Promise<{ message: string; funnel_stage_id: number; is_paused: boolean }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/funnel-stages/${funnelStageId}/pause`,
    { method: 'PUT' },
  );
  if (!response.ok) {
    if (response.status >= 500) throw new Error('Failed to pause funnel stage. Please try again.');
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to pause funnel stage');
  }
  return response.json();
};

export const resumeFunnelStage = async (
  campaignId: number,
  adId: number,
  funnelStageId: number,
): Promise<{ message: string; funnel_stage_id: number; is_paused: boolean }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/funnel-stages/${funnelStageId}/resume`,
    { method: 'PUT' },
  );
  if (!response.ok) {
    if (response.status >= 500) throw new Error('Failed to resume funnel stage. Please try again.');
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || 'Failed to resume funnel stage');
  }
  return response.json();
};

export async function fetchGoogleAdsAccounts(): Promise<GoogleAdsAccount[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/accounts`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const data = await response.json();
  return data;
}

export async function fetchLinkedGoogleAdsAccounts(): Promise<GoogleAdsAccount[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/accounts/linked`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export async function setSelectedGoogleAdsAccount(
  googleAdsAccountId: string,
  organizationId?: number,
): Promise<string> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/accounts/set-selected`, {
    method: 'PUT',
    body: JSON.stringify({
      googleads_account_id: googleAdsAccountId,
      organization_id: organizationId,
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const detail = errorData?.detail || 'Failed to select Google Ads account';
    throw new Error(detail);
  }

  return response.json();
}

export async function generateBusinessDescription({
  url,
}: {
  url: string;
}): Promise<{ description: string; url: string; business_name: string }> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/generate-description?url=${url}`, {
    method: 'POST',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  return response.json();
}

export interface CompetitorData {
  competitor_brand_name: string;
  primary_website: string;
  description?: string;
  social_media?: Record<string, string>;
  location?: Record<string, string>;
  competitor_metadata?: Record<string, unknown>;
  competitor_closeness: number;
}

// Competitor task types for Celery-based generation
export interface CompetitorTaskStartResponse {
  message: string;
  task_tracking_id: number;
  celery_task_id: string;
  business_url: string;
  num_competitors: number;
}

export interface CompetitorTaskStatusResponse {
  task_tracking_id: number;
  state: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  url: string | null;
  created_at: string | null;
  updated_at: string | null;
  error_message?: string;
  result?: {
    message: string;
    status: string;
  } | null;
}

export interface CompetitorTaskResultResponse {
  status: 'in_progress' | 'completed' | 'failed' | 'expired';
  message: string;
  task_tracking_id: number;
  url?: string;
  competitors?: CompetitorData[];
  count?: number;
  error_message?: string;
}

export interface CompetitorProgressUpdate {
  status: 'starting' | 'polling' | 'complete' | 'error';
  message: string;
  data?: CompetitorData[];
}

/**
 * Start competitor generation task (Celery-based)
 */
export async function startCompetitorGeneration(
  url: string,
  numCompetitors: number = 10,
  businessId?: number,
): Promise<CompetitorTaskStartResponse> {
  const queryParams = new URLSearchParams({
    url: url,
    num_competitors: numCompetitors.toString(),
  });
  if (businessId) {
    queryParams.set('business_id', businessId.toString());
  }

  const response = await fetchWithAuth(
    `${apiEndpoint}/business/generate-competitors-full?${queryParams.toString()}`,
    {
      method: 'POST',
    },
  );

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to start competitor generation');
  }

  return response.json();
}

/**
 * Get competitor task status
 */
export async function getCompetitorTaskStatus(
  taskTrackingId: number,
): Promise<CompetitorTaskStatusResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/competitor-task-status/${taskTrackingId}`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get task status');
  }

  return response.json();
}

/**
 * Get competitor task result
 */
export async function getCompetitorTaskResult(
  taskTrackingId: number,
): Promise<CompetitorTaskResultResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/competitor-task-result/${taskTrackingId}`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to get task result');
  }

  return response.json();
}

/**
 * Fetch business competitors using Celery task with polling
 * Starts a background task and polls for completion
 */
export async function fetchBusinessCompetitors(
  url: string,
  onProgress?: (update: CompetitorProgressUpdate) => void,
  numCompetitors: number = 10,
  pollIntervalMs: number = 2000,
  maxPollAttempts: number = 180, // 6 minutes max (180 * 2s)
): Promise<CompetitorData[]> {
  // Notify starting
  onProgress?.({
    status: 'starting',
    message: '🚀 Starting competitor analysis...',
  });

  // Start the Celery task
  const startResponse = await startCompetitorGeneration(url, numCompetitors);
  const { task_tracking_id } = startResponse;

  // Poll for task completion
  let pollAttempt = 0;
  
  const pollMessages = [
    '🔍 Searching for competitors...',
    '🌐 Analyzing market landscape...',
    '📊 Gathering competitor data...',
    '🏢 Processing company information...',
    '📝 Compiling competitor profiles...',
    '✨ Almost there...',
  ];

  while (pollAttempt < maxPollAttempts) {
    // Update progress message periodically
    const messageIndex = Math.min(
      Math.floor(pollAttempt / 10),
      pollMessages.length - 1,
    );
    onProgress?.({
      status: 'polling',
      message: pollMessages[messageIndex],
    });

    // Wait before polling
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    pollAttempt++;

    // Check task status
    const statusResponse = await getCompetitorTaskStatus(task_tracking_id);

    if (statusResponse.state === 'COMPLETED') {
      // Task completed - fetch results
      const resultResponse = await getCompetitorTaskResult(task_tracking_id);

      if (resultResponse.status === 'completed' && resultResponse.competitors) {
        const competitors = resultResponse.competitors.filter(
          (comp): comp is CompetitorData =>
            comp != null && typeof comp === 'object' && 'primary_website' in comp,
        );

        onProgress?.({
          status: 'complete',
          message: `✅ Found ${competitors.length} competitors!`,
          data: competitors,
        });

        return competitors;
      } else if (resultResponse.status === 'expired') {
        throw new Error('Results expired. Please try again.');
      } else {
        throw new Error(resultResponse.message || 'Failed to retrieve results');
      }
    } else if (statusResponse.state === 'FAILED') {
      // Task failed
      const errorMessage =
        statusResponse.error_message || 'Competitor generation failed';
      onProgress?.({
        status: 'error',
        message: `❌ ${errorMessage}`,
      });
      throw new Error(errorMessage);
    }

    // Task still in progress - continue polling
  }

  // Max poll attempts reached
  throw new Error('Competitor generation timed out. Please try again.');
}

export async function fetchBusinessIdealCustomers(url: string): Promise<string[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/generate-ideal-customers?url=${url}`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  return response.json();
}

export async function fetchIsUrlReachable(url: string): Promise<null> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/is-url-reachable?url=${url}`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  return response.json();
}

export async function fetchBusinessProducts(url: string): Promise<string[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/generate-products?url=${url}`, {
    method: 'GET',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }
  return response.json();
}

export async function updateGoogleCampaign(campaignId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/google/campaigns/${campaignId}`, {
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export interface AvailableCompetitor {
  id: string;
  competitor_brand_name: string;
  primary_website?: string | null;
}

/**
 * Competitors linked to a competitor campaign that do not have an ad yet.
 * Returns an empty list for every other campaign type: those vary by landing page, not competitor.
 */
export async function getAvailableCompetitors(campaignId: number): Promise<AvailableCompetitor[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/available-competitors`);
  await assertOk(response);
  return response.json();
}

// competitorId is required for competitor campaigns: the competitor is what the ad targets, and the
// backend rejects the request without it rather than creating an ad it cannot generate (#6252).
export async function createAd(campaignId: number, url: string, competitorId?: string): Promise<Ad> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/`, {
    method: 'POST',
    body: JSON.stringify(competitorId ? { url: url, competitor_id: competitorId } : { url: url }),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    const errorMessage = errorRes.detail || 'An unknown error occurred';
    throw new Error(errorMessage);
  }

  const data = await response.json();

  // Backend creates the ad row synchronously and returns {"status": "Ad creation started", "ad_id": <real_id>, ...}
  // Celery handles content generation (keywords, headlines, descriptions) asynchronously.
  if (data.status === 'Ad creation started') {
    return {
      id: data.ad_id,
      slug: `pending-${data.ad_id}`,
      state: 'DRAFT',
      url: data.url,
      is_paused: false,
      campaign_id: data.campaign_id,
      headlines: [],
      descriptions: [],
      keywords: [],
      negative_keywords: [],
      alternative_keywords: [],
      ad_intents: [],
      scraped_data: undefined,
    } as Ad;
  }

  // If backend returns full Ad object, use it
  return data;
}

// ── AI landing pages (#5701) ──────────────────────────────────────────────────
// Every call carries organization_id: the backend's get_current_organization
// resolves the org from that query param (house convention) and 404s without it.

export async function getLandingPage(publicId: string, organizationId: number): Promise<LandingPage> {
  const response = await fetchWithAuth(`${apiEndpoint}/landing-pages/${publicId}?organization_id=${organizationId}`);
  await assertOk(response, 'Failed to load landing page');
  return response.json();
}

export async function getCampaignLandingPage(campaignId: number, organizationId: number): Promise<LandingPage | null> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/landing-pages?campaign_id=${campaignId}&organization_id=${organizationId}`,
  );
  await assertOk(response, 'Failed to load landing page');
  return response.json();
}

export async function createLandingPage(campaignId: number, organizationId: number): Promise<LandingPage> {
  const response = await fetchWithAuth(`${apiEndpoint}/landing-pages?organization_id=${organizationId}`, {
    method: 'POST',
    body: JSON.stringify({ campaign_id: campaignId }),
  });
  await assertOk(response, 'Failed to generate landing page');
  return response.json();
}

export async function publishLandingPage(publicId: string, organizationId: number): Promise<LandingPage> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/landing-pages/${publicId}/publish?organization_id=${organizationId}`,
    { method: 'POST' },
  );
  await assertOk(response, 'Failed to publish landing page');
  return response.json();
}

export async function unpublishLandingPage(publicId: string, organizationId: number): Promise<LandingPage> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/landing-pages/${publicId}/unpublish?organization_id=${organizationId}`,
    { method: 'POST' },
  );
  await assertOk(response, 'Failed to unpublish landing page');
  return response.json();
}

export async function submitVibeEdit(
  publicId: string,
  organizationId: number,
  instruction: string,
  section: string | null,
): Promise<{ ok: boolean }> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/landing-pages/${publicId}/edits?organization_id=${organizationId}`,
    {
      method: 'POST',
      body: JSON.stringify({ instruction, section }),
    },
  );
  await assertOk(response, 'Failed to submit edit');
  return response.json();
}

export async function getLandingPageLeads(publicId: string, organizationId: number): Promise<LandingPageLead[]> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/landing-pages/${publicId}/leads?organization_id=${organizationId}`,
  );
  await assertOk(response, 'Failed to load leads');
  return response.json();
}

export async function fetchCampaignTaskTracking(campaignId: number): Promise<TaskTrackingResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/task-tracking`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function fetchUsers(): Promise<User[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/users`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function impersonateUser(email: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/user/${email}/impersonate`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function unsetImpersonateUser() {
  const response = await fetchWithAuth(`${apiEndpoint}/user/unset_impersonate`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function copyCampaign(campaignId: number, name?: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/copy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}


export async function restartAd(campaignId: number, adId: number): Promise<{
  ad_id: number | null;
  campaign_id: number;
  task_tracking_id: number;
  restart_attempt: number;
  max_attempts: number;
  campaign_type: string;
  url: string;
  message?: string;
}> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/restart`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to rebuild ad' }));
    throw new Error(error.detail || 'Failed to rebuild ad');
  }
  return response.json();
}

export async function restartCampaign(campaignId: number): Promise<{
  campaign_id: number;
  task_tracking_id: number;
  restart_attempt: number;
  max_attempts: number;
  campaign_type: string;
}> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/restart`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to restart campaign');
  }
  return response.json();
}

export async function transferCampaignsToOrganization(payload: {
  campaign_ids: number[];
  target_organization_id: number;
  source_organization_id: number;
}) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/transfer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to transfer campaigns');
  }

  return response.json();
}

export async function transferCampaignToGoogleAdsAccount(payload: {
  campaign_id: number;
  target_googleads_account_id: string;
}) {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${payload.campaign_id}/transfer-googleads-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      target_googleads_account_id: payload.target_googleads_account_id,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to transfer campaign to Google Ads account');
  }
  return response.json();
}

export async function linkGoogleAdsCustomer(googleAdsCustomerId: number, name: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/google/link-accounts`, {
    method: 'POST',
    body: JSON.stringify({
      account_id: googleAdsCustomerId,
      name: name,
    }),
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export const regenerateCampaignTcpa = async (campaignId: number): Promise<TcpaRegenerationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/tcpa`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

export async function fetchOrganizations(): Promise<OrganizationDataWithMembership[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations`, {
    method: 'GET',
  });

  if (!response.ok) {
    console.error('  ** [API] Failed to fetch organizations:', response.status);
    throw new Error(`Network response was not ok: ${response.status}`);
  }

  const data = await response.json();

  return data;
}

export async function createOrganization(organization: OrganizationCreate) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations`, {
    method: 'POST',
    body: JSON.stringify(organization),
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function fetchOrganization(organizationId: number): Promise<OrganizationDataWithUsersMembership> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  const data = await response.json();
  return data;
}

export async function fetchBudgetStatus(organizationId: number): Promise<BudgetStatusResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/budget/status`);
  if (!response.ok) {
    throw new Error('Failed to fetch budget status');
  }
  return response.json();
}

export async function deleteOrganization(organizationId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json();
    return { msg: errorData.detail || 'An unexpected error occurred' };
  }
  if (response.status !== 204) {
    return await response.json();
  }

  return null;
}

export async function cancelOrganizationInvite(organizationId: number, email: string) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/organizations/${organizationId}/invites/${encodeURIComponent(email)}`,
    {
      method: 'DELETE',
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Failed to cancel invitation');
  }

  return true;
}

export async function resendOrganizationInvite(organizationId: number, email: string): Promise<{ message: string }> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/organizations/${organizationId}/invites/${encodeURIComponent(email)}/resend`,
    {
      method: 'POST',
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Failed to resend invitation');
  }

  return response.json();
}

export async function removeUserFromOrganization(organizationId: number, userId: number) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/users/${userId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json();
    return { msg: errorData.detail || 'An unexpected error occurred' };
  }
  if (response.status !== 204) {
    return await response.json();
  }

  return null;
}

export async function updateOrganization(organization: OrganizationData) {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organization.id}`, {
    method: 'PUT',
    body: JSON.stringify(organization),
  });
  if (!response.ok) {
    const errorData = await response.json();
    return { msg: errorData.detail || 'An unexpected error occurred' };
  }
  return response.json();
}

// Update the org-level budget cap (max_spend). Separate from updateOrganization because:
// (1) it hits a dedicated ADMIN-gated endpoint (#5597 — admins manage budgets; the full org PUT is
//     owner-only, which silently 403'd admins and made the Maximum daily budget revert on refresh);
// (2) it THROWS on failure (via assertOk → typed ApiError), so the budget save surfaces the error
//     instead of optimistically lying.
export async function updateOrganizationBudget(organizationId: number, maxSpend: number): Promise<OrganizationData> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/budget`, {
    method: 'PUT',
    body: JSON.stringify({ max_spend: maxSpend }),
  });
  await assertOk(response, 'Failed to update organization budget');
  return response.json();
}

export async function sendInvitationToUser(email: string, role: string, organizationId: number): Promise<InvitationResponse> {
  const url = `${apiEndpoint}/organizations/${organizationId}/invite`;

  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify({ email, role });

  const response = await fetchWithAuth(url, {
    method: 'POST',
    headers: headers,
    body: body,
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorData = await response.json();

      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData.detail) {
        errorMessage = typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
      } else if (errorData.message) {
        errorMessage = typeof errorData.message === 'string' ? errorData.message : JSON.stringify(errorData.message);
      } else if (errorData.error) {
        errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
      } else {
        errorMessage = JSON.stringify(errorData);
      }
    } catch {
      // If we can't parse the error response, use the default message
    }
    throw new Error(errorMessage);
  }
  return response.json();
}

export async function verifyInviteCode(code: string): Promise<{ email: string; is_valid_refresh_token: boolean }> {
  const response = await fetchWithAuth(`${apiEndpoint}/auth/verify-invite-code/${code}`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function fetchCampaignActionLogs(slug: string): Promise<ActionLog[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${slug}/action-logs`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  const data = await response.json();
  return data;
}

export async function triggerZeroImpressionOptimisation(slug: string): Promise<string> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${slug}/optimisations/zeroimpression`, {
    method: 'POST',
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'An unknown error occurred');
  }

  const data = await response.json();
  return data;
}

export async function fetchPerformanceReport(
  timeRange: 'daily' | 'weekly',
  dateRange: string,
  campaignId: string | number,
  signal?: AbortSignal,
  /**
   * #4319 — when both are provided, the backend ignores `dateRange` and
   * fetches the explicit ISO range instead. Used by the perf-v2 chart to
   * drive the previous-period overlay for week / month / year compare modes
   * (which can't be expressed as a relative "30d" string).
   */
  startDate?: string,
  endDate?: string,
): Promise<PerformanceData[]> {
  const params = new URLSearchParams();
  if (startDate && endDate) {
    params.set('start_date', startDate);
    params.set('end_date', endDate);
  }
  const qs = params.toString();
  const endpoint =
    `${apiEndpoint}/google/performance/${timeRange}/${dateRange}/${campaignId}` +
    (qs ? `?${qs}` : '');

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export interface BiddingStrategyMetrics {
  id: string;
  name: string;
  type?: string;
  status?: string;
  target_cpa?: number;
  target_roas?: number;
  clicks: number;
  impressions: number;
  cost_micros: number;
  cost: number;
  conversions: number;
  all_conversions?: number;
  all_conversions_value?: number;
  view_through_conversions?: number;
  conversions_value: number;
}

export interface BiddingStrategyReportResponse {
  strategies: BiddingStrategyMetrics[];
  count: number;
  date_range: string;
}

export interface AdPerformanceData {
  ad_id: string;
  ad_name: string;
  ad_type?: string;
  ad_group_ad_status?: string;
  ad_group_id: string;
  ad_group_name: string;
  campaign_id: string;
  funnel_stage?: string | null;
  campaign_name: string;
  headlines: string[];
  descriptions: string[];
  /** First landing-page URL (`ad_group_ad.ad.final_urls[0]`) — null for ads without URLs (Display/Video). #4308. */
  final_url?: string | null;
  /** Full landing-page URL list — RSAs typically have one, smart ads sometimes have multiple. */
  final_urls?: string[];
  clicks: number;
  impressions: number;
  cost_micros: number;
  cost: number;
  conversions: number;
  all_conversions?: number;
  all_conversions_value?: number;
  view_through_conversions?: number;
  ctr: number;
  cvr: number;
  cpc: number;
}

export async function fetchBiddingStrategyReport(
  dateRange: string = 'LAST_7_DAYS',
  campaignId?: number,
  signal?: AbortSignal,
): Promise<BiddingStrategyReportResponse> {
  const params = new URLSearchParams();
  params.append('date_range', dateRange);
  if (campaignId) {
    params.append('campaign_id', campaignId.toString());
  }

  const endpoint = `${apiEndpoint}/google/reports/bidding-strategy?${params.toString()}`;

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export async function fetchAdPerformanceReport(
  campaignIds?: string,
  dateRange: string = 'LAST_30_DAYS',
  signal?: AbortSignal,
  startDate?: string,
  endDate?: string,
): Promise<AdPerformanceData[]> {
  const params = new URLSearchParams();
  // Custom start_date / end_date take precedence on the backend (#4293);
  // we still send date_range so it's used when start/end are absent.
  params.append('date_range', dateRange);
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  if (startDate) {
    params.append('start_date', startDate);
  }
  if (endDate) {
    params.append('end_date', endDate);
  }

  const endpoint = `${apiEndpoint}/google/reports/ads?${params.toString()}`;

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export interface CampaignStatusData {
  campaign_id: string;
  campaign_name: string;
  /** Google Ads campaign-level primary_status (e.g. "LIMITED", "ELIGIBLE", "PAUSED"). */
  primary_status: string | null;
  /** Repeated enum values explaining the status — e.g. ["BUDGET_CONSTRAINED"]. */
  primary_status_reasons: string[];
  /** 0.0-1.0 from Google Ads; the UI renders ×100. */
  optimization_score: number | null;
  bidding_strategy_type: string | null;
  /** Live Google Ads daily budget in currency units (#3960 fallback when the Glitch DB is stale). */
  daily_budget: number | null;
  impressions: number;
  /** 0.0-1.0 ratios — formatted to % on the frontend. */
  search_impression_share: number | null;
  search_budget_lost_impression_share: number | null;
  search_rank_lost_impression_share: number | null;
}

export async function fetchCampaignStatusReport(
  campaignIds: string,
  startDate: string,
  endDate: string,
  signal?: AbortSignal,
): Promise<CampaignStatusData[]> {
  const params = new URLSearchParams();
  params.append('campaign_ids', campaignIds);
  params.append('start_date', startDate);
  params.append('end_date', endDate);

  const endpoint = `${apiEndpoint}/google/reports/campaign-status?${params.toString()}`;

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch campaign status report');
  }

  return response.json();
}

export interface KeywordOverviewData {
  campaign_id: string;
  campaign_name: string;
  ad_group_id: string;
  ad_group_name: string;
  criterion_id: string;
  keyword: string;
  /** GAds enum like "BROAD", "PHRASE", "EXACT". */
  match_type: string | null;
  /** GAds enum like "ENABLED", "PAUSED", "REMOVED". */
  status: string | null;
  /** 1-10 (point-in-time, no date segmentation — see #2819). Null when GAds returns no QS. */
  quality_score: number | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  cost: number;
  conversions: number;
  conv_rate: number;
  cost_per_conv: number;
  ctr: number;
  avg_cpc: number;
  /** 0.0-1.0 ratios. */
  search_impression_share: number | null;
  search_exact_match_impression_share: number | null;
  search_budget_lost_impression_share: number | null;
  search_rank_lost_impression_share: number | null;
}

export interface AccountSearchTerm {
  search_term: string;
  /** Search-term match type as it triggered against the keyword: BROAD / PHRASE / EXACT / NEAR_EXACT etc. */
  match_type: string | null;
  /** ADDED · EXCLUDED · NONE — search_term_view.status. */
  status: string | null;
  campaign_id: string;
  campaign_name: string;
  ad_group_id: string;
  ad_group_name: string;
  /** The keyword text that triggered this search term. */
  keyword: string;
  keyword_match_type: string | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  cost: number;
  conversions: number;
  ctr: number;
  avg_cpc: number;
  conv_rate: number;
  cost_per_conv: number;
}

export interface RecommendationItem {
  id: number;
  campaign_id: number;
  campaign_name: string;
  recommendation_type: string;
  title: string;
  description: string;
  content: string;
  markdown_content: string | null;
  is_applied: boolean;
  status: string | null;
  rejected_at: string | null;
  reject_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecommendationListResponse {
  items: RecommendationItem[];
  total: number;
  skip: number;
  limit: number;
}

export async function fetchRecommendations(
  options?: { state?: string; recommendationType?: string; limit?: number; skip?: number },
  signal?: AbortSignal,
): Promise<RecommendationListResponse> {
  const params = new URLSearchParams();
  if (options?.state) params.append('state', options.state);
  if (options?.recommendationType) params.append('recommendation_type', options.recommendationType);
  if (options?.limit !== undefined) params.append('limit', String(options.limit));
  if (options?.skip !== undefined) params.append('skip', String(options.skip));
  const qs = params.toString();
  const endpoint = `${apiEndpoint}/recommendations${qs ? `?${qs}` : ''}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch recommendations');
  }
  return response.json();
}

export type InsightSentiment = 'info' | 'success' | 'warning' | 'opportunity';

export interface PerformanceInsight {
  sentiment: InsightSentiment;
  /** SHORT uppercase label — 1-3 words. Anchors the card. */
  category: string;
  /** 2-3 sentence narrative quoting real numbers. */
  narrative: string;
}

export interface PerformanceInsightsResponse {
  insights: PerformanceInsight[];
}

export async function fetchPerformanceInsights(
  startDate: string,
  endDate: string,
  campaignIds: string,
  signal?: AbortSignal,
): Promise<PerformanceInsightsResponse> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  params.append('campaign_ids', campaignIds);
  const endpoint = `${apiEndpoint}/google/reports/insights?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch performance insights');
  }
  return response.json();
}

export interface ConversionActionRow {
  campaign_id: string;
  campaign_name: string;
  conversion_action_id: string | null;
  conversion_action_name: string | null;
  /** Enum string from Google Ads — SUBMIT_LEAD_FORM, PHONE_CALL_LEAD, PURCHASE, … */
  conversion_action_category: string | null;
  conversions: number;
  all_conversions: number;
  all_conversions_value: number;
  cost_micros: number;
  cost: number;
  cost_per_conv: number;
}

export async function fetchAccountConversionActionsReport(
  startDate: string,
  endDate: string,
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<ConversionActionRow[]> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/conversion-actions?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch conversion-actions report');
  }
  return response.json();
}

export type DemographicDimension = 'age' | 'gender' | 'parental_status';

export interface DemographicRow {
  dimension: DemographicDimension;
  /** Enum string from Google Ads (e.g. "AGE_RANGE_35_44", "MALE", "PARENT"). */
  segment: string | null;
  campaign_id: string;
  campaign_name: string;
  ad_group_id: string;
  ad_group_name: string;
  status: string | null;
  /** Bid adjustment as a multiplier (1.0 = no adjustment, 1.2 = +20%). Null when unset. */
  bid_modifier: number | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  cost: number;
  conversions: number;
  ctr: number;
  avg_cpc: number;
  conv_rate: number;
  cost_per_conv: number;
}

export async function fetchAccountDemographicsReport(
  dimension: DemographicDimension,
  startDate: string,
  endDate: string,
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<DemographicRow[]> {
  const params = new URLSearchParams();
  params.append('dimension', dimension);
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/demographics?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch demographics report');
  }
  return response.json();
}

// #5922 — account-wide geographic performance, one row per country.
export interface GeographyRow {
  country: string;
  /** Google Ads geo_target_constant criterion id (country). */
  criterion_id: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  avg_cpc: number;
  conv_rate: number;
  cost_per_conv: number;
}

export async function fetchAccountGeographyReport(
  startDate: string,
  endDate: string,
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<GeographyRow[]> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/geography?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  await assertOk(response, 'Failed to fetch geography report');
  return response.json();
}

// #5922 — account-wide device performance, one row per device.
export interface DeviceRow {
  device: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  avg_cpc: number;
  conv_rate: number;
  cost_per_conv: number;
}

export async function fetchAccountDeviceReport(
  startDate: string,
  endDate: string,
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<DeviceRow[]> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/devices?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  await assertOk(response, 'Failed to fetch device report');
  return response.json();
}

export interface ActivityEvent {
  /** 'user' for Google Ads change_event rows, 'glitch' for ActionLog rows. */
  source: 'user' | 'glitch';
  /** ISO datetime string. */
  timestamp: string | null;
  /** user_email for 'user', 'Glitch' for 'glitch'. */
  actor: string | null;
  /** Google Ads client_type ('GOOGLE_ADS_WEB_CLIENT' etc.) or 'GLITCH_OPTIMISATION'. */
  actor_kind: string | null;
  /** Google Ads change_resource_type ('CAMPAIGN_BUDGET', 'AD_GROUP_CRITERION'…) or Glitch ActionType. */
  resource_type: string | null;
  /** CREATE / UPDATE / REMOVE. Null for Glitch entries. */
  operation: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  ad_group_id: string | null;
  ad_group_name: string | null;
  /** FieldMask paths for user edits (e.g. ['amount_micros']); empty for Glitch. */
  changed_fields: string[];
  /** Free-text description from the Glitch ActionLog. Null for user edits. */
  summary: string | null;
  /** Glitch-only — deep-link into the existing Optimisations / undo surface. */
  action_log_id?: number | null;
  is_undone?: boolean | null;
}

export async function fetchActivityFeed(
  startDate: string,
  endDate: string,
  organizationId: number,
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<ActivityEvent[]> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  params.append('organization_id', organizationId.toString());
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/activity-feed?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch activity feed');
  }
  return response.json();
}

export async function fetchAccountSearchTermsReport(
  startDate: string,
  endDate: string,
  campaignIds?: string,
  minImpressions: number = 1,
  signal?: AbortSignal,
): Promise<AccountSearchTerm[]> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (minImpressions !== 1) {
    params.append('min_impressions', minImpressions.toString());
  }
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/account-search-terms?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch account search terms report');
  }
  return response.json();
}

export async function fetchKeywordsOverviewReport(
  startDate: string,
  endDate: string,
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<KeywordOverviewData[]> {
  const params = new URLSearchParams();
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const endpoint = `${apiEndpoint}/google/reports/keywords-overview?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  if (!response.ok) {
    throw new Error('Failed to fetch keywords overview report');
  }
  return response.json();
}

// #5997 — per-ad-group landing-page experience (post_click_quality_score) buckets.
export interface LandingPageExperienceRow {
  ad_group_id: string;
  above: number;
  average: number;
  below: number;
}

// Point-in-time (NO_DATE), so no date params. The Landing Pages view rolls these
// up to URLs via keyword -> ad group -> ad final_url.
export async function fetchLandingPageExperience(
  campaignIds?: string,
  signal?: AbortSignal,
): Promise<LandingPageExperienceRow[]> {
  const params = new URLSearchParams();
  if (campaignIds) {
    params.append('campaign_ids', campaignIds);
  }
  const qs = params.toString();
  const endpoint = `${apiEndpoint}/google/reports/landing-page-experience${qs ? `?${qs}` : ''}`;
  const response = await fetchWithAuth(endpoint, { method: 'GET', signal });
  await assertOk(response, 'Failed to fetch landing-page experience');
  return response.json();
}

export interface KeywordPerformanceData {
  campaign_id: string;
  campaign_name: string;
  keyword: string;
  ad_group_id?: string;
  ad_group_name?: string;
  keyword_id?: string;
  match_type?: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  all_conversions?: number;
  all_conversions_value?: number;
  view_through_conversions?: number;
  ctr: number;
  cvr: number;
  cpc: number;
  search_impression_share?: number | null;
  search_top_impression_share?: number | null;
  search_absolute_top_impression_share?: number | null;
  search_rank_lost_impression_share?: number | null;
  search_budget_lost_impression_share?: number | null;
  first_page_cpc?: number | null;
  quality_score?: number | null;
  first_position_cpc?: number | null;
}

export async function fetchKeywordPerformance(
  keywordText: string,
  campaignId?: number,
  adGroupId?: string,
  dateRange: string = 'LAST_30_DAYS',
  signal?: AbortSignal,
): Promise<KeywordPerformanceData | null> {
  const params = new URLSearchParams();
  params.append('keyword_text', keywordText);
  params.append('date_range', dateRange);
  if (campaignId) {
    params.append('campaign_id', campaignId.toString());
  }
  if (adGroupId) {
    params.append('ad_group_id', adGroupId);
  }

  const endpoint = `${apiEndpoint}/google/reports/keyword-performance?${params.toString()}`;

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null; // Keyword not found in performance data
    }
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export async function fetchKeywordPerformanceBatch(
  campaignId: number,
  dateRange: string = 'LAST_30_DAYS',
  signal?: AbortSignal,
): Promise<KeywordPerformanceData[]> {
  const params = new URLSearchParams();
  params.append('campaign_id', campaignId.toString());
  params.append('date_range', dateRange);

  const endpoint = `${apiEndpoint}/google/reports/keyword-performance/batch?${params.toString()}`;

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

export async function fetchOnboardingAdLinks({
  url,
  products,
  onProgress,
}: {
  url: string;
  products: string[];
  onProgress?: (update: { status: string; message: string; data?: string[] }) => void;
}): Promise<string[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/generate-onboarding-ad-links`, {
    method: 'POST',
    body: JSON.stringify({ url, products }),
  });

  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail);
  }

  // Check if it's a streaming response
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('text/plain') && onProgress) {
    // Handle streaming response
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let finalResult: string[] = [];

    if (reader) {
      try {
         
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                onProgress(data);

                // Save final result when complete
                if (data.status === 'final' && data.data) {
                  finalResult = data.data;
                }
              } catch (e) {
                console.warn('Failed to parse streaming data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    }

    return finalResult;
  } else {
    // Handle regular JSON response (fallback)
    return response.json();
  }
}

// Campaign generation activity feed — backend exposes /campaigns/{id}/events
// (snapshot) and /events/stream (`data: {json}\n\n`). EventSource isn't used:
// it can't carry the bearer token.

export type ProgressStage =
  | 'reading_page'
  | 'understanding_business'
  | 'finding_angle'
  | 'researching_keywords'
  | 'writing_headlines'
  | 'writing_descriptions'
  | 'generating_sitelinks'
  | 'building_ad'
  | 'setting_bid_strategy'
  | 'finalized'
  // Meta generation stages (#5981) — building_ad and finalized are shared with Google.
  | 'writing_ad_copy'
  | 'uploading_image';

export type ProgressStatus = 'started' | 'completed' | 'failed';

export interface ProgressEvent {
  stage: ProgressStage;
  status: ProgressStatus;
  message: string;
  timestamp: string;
  ad_group_url?: string | null;
  funnel_stage?: 'upper' | 'middle' | 'lower' | null;
  artifacts?: Record<string, unknown> | null;
  error?: string | null;
}

export async function fetchCampaignEventsHistory(campaignId: number): Promise<ProgressEvent[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/events`);
  if (!response.ok) {
    throw new Error(`Failed to fetch campaign events: ${response.status}`);
  }
  return response.json();
}

/**
 * Generation ACTIVITY-feed history for a Meta campaign (#5981) — the Meta sibling of
 * `fetchCampaignEventsHistory`. Polled (no stream endpoint): the Meta panel already polls
 * generation progress every 3s, and the feed rides the same cadence.
 */
export async function fetchMetaCampaignEventsHistory(slug: string): Promise<ProgressEvent[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/events`);
  await assertOk(response, 'Failed to fetch Meta campaign events');
  return response.json();
}

export function subscribeCampaignEvents(
  campaignId: number,
  onEvent: (event: ProgressEvent) => void,
  onError?: (err: unknown) => void,
): AbortController {
  const controller = new AbortController();

  (async () => {
    try {
      const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/events/stream`, {
        method: 'GET',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream subscription failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Stream subscription returned no body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

       
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Buffer partial trailing events across chunks.
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const chunk = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf('\n\n');

          for (const line of chunk.split('\n')) {
            if (line.startsWith('data: ')) {
              try {
                const event = JSON.parse(line.slice(6)) as ProgressEvent;
                onEvent(event);
              } catch (parseErr) {
                console.warn('Failed to parse campaign event:', parseErr);
              }
            }
          }
        }
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      onError?.(err);
    }
  })();

  return controller;
}

export async function getConversionTrackingSettings(): Promise<ConversionTrackingSettings> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/conversion-tracking`);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

export async function createWebsiteConversion(
  request: WebsiteConversionCreateRequest
): Promise<ConversionAction> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/conversion-actions/website`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to create website conversion');
  }
  return response.json();
}

export async function createCallConversion(
  request: CallConversionCreateRequest
): Promise<ConversionAction> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/conversion-actions/call`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to create call conversion');
  }
  return response.json();
}

export async function listConversionActions(): Promise<ConversionActionListResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/conversion-actions`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to list conversion actions');
  }
  return response.json();
}

export async function getConversionActionTrackingSnippet(
  conversionActionId: string
): Promise<{ tracking_snippet: TrackingSnippet | null; phone_number: string | null }> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/google/conversion-actions/${conversionActionId}/tracking-snippet`
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Failed to fetch tracking snippet');
  }
  return response.json();
}

export async function removeConversionAction(conversionActionId: string): Promise<{ message: string }> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/conversion-actions/${conversionActionId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to remove conversion action');
  }
  return response.json();
}

export async function updateConversionAction(
  conversionActionId: string,
  request: ConversionActionUpdateRequest
): Promise<ConversionAction> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/conversion-actions/${conversionActionId}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to update conversion action');
  }
  return response.json();
}

export const fetchActionLogs = async (
  campaignId: number,
  filters: ActionFilter,
  pagination: PaginationParams,
): Promise<ActionLogResponse> => {
  const queryParams = new URLSearchParams();

  // Add pagination
  queryParams.append('skip', pagination.skip.toString());
  queryParams.append('limit', pagination.limit.toString());

  // Add filters
  if (filters.action_type) {
    queryParams.append('action_type', filters.action_type);
  }

  if (filters.is_undone !== undefined) {
    queryParams.append('is_undone', filters.is_undone.toString());
  }

  if (filters.start_date) {
    queryParams.append('start_date', filters.start_date);
  }

  if (filters.end_date) {
    queryParams.append('end_date', filters.end_date);
  }

  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/actions?${queryParams.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch action logs');
  }

  return response.json();
};

export const fetchAllActionLogs = async (
  filters: ActionFilter,
  pagination: PaginationParams,
): Promise<CampaignActionsResponse> => {
  const queryParams = new URLSearchParams();

  // Add pagination
  queryParams.append('skip', pagination.skip.toString());
  queryParams.append('limit', pagination.limit.toString());

  // Add filters
  if (filters.action_type) {
    queryParams.append('action_type', filters.action_type);
  }

  if (filters.is_undone !== undefined) {
    queryParams.append('is_undone', filters.is_undone.toString());
  }

  if (filters.start_date) {
    queryParams.append('start_date', filters.start_date);
  }

  if (filters.end_date) {
    queryParams.append('end_date', filters.end_date);
  }

  if (filters.audit_status) {
    queryParams.append('audit_status', filters.audit_status);
  }

  if (filters.organization_id !== undefined) {
    queryParams.append('organization_id', filters.organization_id.toString());
  }

  const response = await fetchWithAuth(`${apiEndpoint}/actions/all?${queryParams.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch all action logs');
  }

  return response.json();
};

export const fetchTasksForCampaign = async (campaignId: number, limit = 5): Promise<ActionLogResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit.toString());

  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/actions?${queryParams.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch campaign tasks');
  }

  return response.json();
};

export const undoAction = async (actionId: number): Promise<void> => {
  const response = await fetchWithAuth(`${apiEndpoint}/actions/${actionId}/undo`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to undo action');
  }

  return response.json();
};

/**
 * Generic function to fetch data from the API with proper typing
 * @param endpoint The API endpoint to fetch from (without the base URL)
 * @returns A promise resolving to the typed data
 */
export async function fetchData<T>(endpoint: string): Promise<T> {
  const response = await fetchWithAuth(`${apiEndpoint}${endpoint}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `Failed to fetch data from ${endpoint}`);
  }

  return response.json();
}

export async function fetchAdSlug(adId: number): Promise<string | null> {
  try {
    const response = await fetchWithAuth(`${apiEndpoint}/ads/${adId}/slug`, {
      method: 'GET',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.slug || null;
  } catch (error) {
    console.error('Error fetching ad slug:', error);
    return null;
  }
}

export async function pauseNegativeKeywords(
  campaignId: number,
  keywords: Array<{ googleads_ad_group_id: string; keyword: string }>,
): Promise<KeywordOperationResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/negative-keywords`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(keywords),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to pause negative keywords');
  }

  return response.json();
}

// Admin queries are exported directly from the admin-queries.ts file
// and should only be imported in admin-specific components

export const fetchNotificationItems = async (
  filters: NotificationFilter,
  page: number = 1,
  page_size: number = 50,
): Promise<NotificationResponse> => {
  const queryParams = new URLSearchParams();

  // Add pagination
  queryParams.append('page', page.toString());
  queryParams.append('page_size', page_size.toString());

  // Add filters
  if (filters.is_viewed !== undefined) {
    queryParams.append('is_viewed', filters.is_viewed.toString());
  }

  if (filters.is_archived !== undefined) {
    queryParams.append('is_archived', filters.is_archived.toString());
  }

  // Multi-select filters: append each value as a separate query parameter
  if (filters.sources && filters.sources.length > 0) {
    filters.sources.forEach((source) => {
      queryParams.append('sources', source);
    });
  }

  if (filters.warning_levels && filters.warning_levels.length > 0) {
    filters.warning_levels.forEach((level) => {
      queryParams.append('warning_levels', level);
    });
  }

  if (filters.start_date) {
    queryParams.append('start_date', filters.start_date);
  }

  if (filters.end_date) {
    queryParams.append('end_date', filters.end_date);
  }

  if (filters.search_query) {
    queryParams.append('search_query', filters.search_query);
  }

  const url = `${apiEndpoint}/organizations/${filters.organization_id}/notifications?${queryParams.toString()}`;

  const response = await fetchWithAuth(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch notification items');
  }

  const data = await response.json();

  return data;
};

export const updateNotificationItem = async (
  id: string,
  updates: Partial<Pick<NotificationItem, 'is_viewed' | 'is_archived'>>,
): Promise<void> => {
  const url = `${apiEndpoint}/notifications/${id}`;

  const response = await fetchWithAuth(url, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update notification item');
  }

  const data = await response.json();
  return data;
};

export const bulkUpdateNotifications = async (
  notificationIds: string[],
  action: import('./types').BulkNotificationAction,
): Promise<{ updated_count: number; message: string }> => {
  const url = `${apiEndpoint}/notifications/bulk-update`;

  const updates: { notification_ids: string[]; is_viewed?: boolean; is_archived?: boolean } = {
    notification_ids: notificationIds,
  };

  switch (action) {
    case 'mark_read':
      updates.is_viewed = true;
      break;
    case 'mark_unread':
      updates.is_viewed = false;
      break;
    case 'archive':
      updates.is_archived = true;
      break;
    case 'unarchive':
      updates.is_archived = false;
      break;
  }

  const response = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to bulk update notifications');
  }

  return response.json();
};

export const fetchNotificationCounts = async (
  organizationId: number,
  filters?: Partial<NotificationFilter>,
): Promise<import('./types').NotificationCounts> => {
  const queryParams = new URLSearchParams();

  if (filters?.is_archived !== undefined) {
    queryParams.append('is_archived', String(filters.is_archived));
  }
  if (filters?.sources && filters.sources.length > 0) {
    filters.sources.forEach((source) => {
      queryParams.append('sources', source);
    });
  }
  if (filters?.warning_levels && filters.warning_levels.length > 0) {
    filters.warning_levels.forEach((level) => {
      queryParams.append('warning_levels', level);
    });
  }
  if (filters?.start_date) {
    queryParams.append('start_date', filters.start_date);
  }
  if (filters?.end_date) {
    queryParams.append('end_date', filters.end_date);
  }
  if (filters?.search_query) {
    queryParams.append('search_query', filters.search_query);
  }

  const url = `${apiEndpoint}/organizations/${organizationId}/notifications/counts?${queryParams.toString()}`;
   const response = await fetchWithAuth(url, {
    method: 'GET',
  });

  if (!response.ok) {
     const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch notification counts');
  }

  return response.json();
};

/**
 * Fetch reports for an organization
 */
export const fetchOrganizationReports = async (
  organizationId: number,
  page: number = 1,
  pageSize: number = 50,
  reportType?: string,
): Promise<import('./types').Report[]> => {
  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('page_size', pageSize.toString());

  if (reportType) {
    queryParams.append('report_type', reportType);
  }

  const url = `${apiEndpoint}/organizations/${organizationId}/reports?${queryParams.toString()}`;

  const response = await fetchWithAuth(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch reports');
  }

  return response.json();
};

export const fetchInvoices = async (organizationId: number): Promise<InvoiceHistoryResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/billing/invoices`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch invoices');
  }

  return response.json();
};

export const cancelSubscription = async (organizationId: number): Promise<CancelSubscriptionResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/billing/cancel-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ at_period_end: true }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to cancel subscription');
  }

  return response.json();
};

// HubSpot Integration API Functions
export const getHubSpotIntegrationStatus = async (): Promise<HubSpotIntegrationStatus> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/integration/status`);
  await assertOk(response);
  return response.json();
};

export const getHubSpotOAuthUrl = async (): Promise<HubSpotOAuthUrlResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/auth/url`);
  await assertOk(response);
  return response.json();
};

export const createHubSpotIntegration = async (data: HubSpotCallbackRequest): Promise<HubSpotIntegrationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/integration/create`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  await assertOk(response);
  return response.json();
};

export const disconnectHubSpotIntegration = async (): Promise<HubSpotIntegrationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/integration`, {
    method: 'DELETE',
  });
  await assertOk(response);
  return response.json();
};

export const refreshHubSpotTokens = async (): Promise<HubSpotIntegrationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/integration/refresh`, {
    method: 'POST',
  });
  await assertOk(response);
  return response.json();
};

export const getHubSpotPipelines = async (organizationId: number): Promise<HubSpotAuditResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/pipelines?organization_id=${organizationId}`);
  await assertOk(response);
  return response.json();
};

export const saveHubSpotPipelineConfig = async (
  organizationId: number,
  data: PipelineConfigCreateRequest
): Promise<PipelineConfigResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/pipeline-config?organization_id=${organizationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await assertOk(response);
  return response.json();
};

export const getHubSpotPipelineConfig = async (organizationId: number): Promise<PipelineConfigResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/hubspot/pipeline-config?organization_id=${organizationId}`);
  if (!response.ok) {
    if (response.status === 404) return null as unknown as PipelineConfigResponse;
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const setupHubSpotConversions = async (
  organizationId: number,
  configId: number
): Promise<{ success: boolean; actions: Array<{ stage_id: string; conversion_action_id: string; name: string; status: string; registry_id?: number }> }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/hubspot/conversion-setup/${configId}?organization_id=${organizationId}`,
    { method: 'POST' }
  );
  await assertOk(response);
  return response.json();
};

// Meta Ads Integration API Functions
export const getMetaIntegrationStatus = async (): Promise<MetaIntegrationStatus> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/integration/status`);
  await assertOk(response);
  return response.json();
};

export const getMetaOAuthUrl = async (): Promise<MetaOAuthUrlResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/auth/url`);
  await assertOk(response);
  return response.json();
};

export const createMetaIntegration = async (data: MetaCallbackRequest): Promise<MetaIntegrationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/integration`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  await assertOk(response);
  return response.json();
};

/** Disconnect the Google Ads connection (#5936). Mirrors disconnectMetaIntegration. */
export const disconnectGoogleAds = async (): Promise<GoogleIntegrationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/google/integration`, { method: 'DELETE' });
  await assertOk(response);
  return response.json();
};

/** Exchange a Google OAuth auth-code for the current user. JWT-anchored connect — deliberately
 * NOT /google/auth, which anchors to the OAuth identity and can save accounts under a different
 * Glitch user (#4152). Same shape as the Meta + HubSpot connect endpoints. */
export const connectGoogleIntegration = async (code: string): Promise<void> => {
  const response = await fetchWithAuth(`${apiEndpoint}/google/integrations/connect`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  await assertOk(response);
};

/** Re-scan the connected Google login for accessible Ads accounts. */
export const refreshGoogleAdsAccounts = async (): Promise<{ accounts_found: number; errors?: unknown[] }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/google/accounts/refresh`, { method: 'POST' });
  await assertOk(response);
  return response.json();
};

export const disconnectMetaIntegration = async (): Promise<MetaIntegrationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/integration`, {
    method: 'DELETE',
  });
  await assertOk(response);
  return response.json();
};

/**
 * A Meta phase-campaign create that was rejected with a 409 — a creation for this offer URL is
 * already in progress (the duplicate-submission guard; since #5982 an existing campaign no longer
 * blocks another). Thrown so the wizard can surface this expected conflict distinctly (inline,
 * non-alarming) rather than as a generic failure toast (#5742 M-2).
 */
export class MetaCampaignConflictError extends Error {}

/**
 * Read a FastAPI error response into a string-safe message.
 *
 * FastAPI returns ``detail`` in three shapes:
 * - 4xx with custom HTTPException ⇒ string (``detail: "Bad request"``)
 * - 422 validation errors ⇒ array of ``{loc, msg, type}`` objects
 * - Some custom paths ⇒ object
 *
 * Throwing ``new Error(detail)`` against the array/object shapes coerces via ``toString()`` and
 * yields ``"[object Object]"`` (the bug bot review #4323 + a user-reported regression both
 * surfaced). This helper normalises all three to a real string so callers can do
 * ``throw new Error(safe)`` without leaking ``[object Object]`` into the UI.
 */
const safeErrorDetail = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.clone().json();
    const detail = body?.detail;
    if (typeof detail === 'string' && detail) return detail;
    if (Array.isArray(detail)) {
      // FastAPI validation errors — join the ``msg`` fields for a human-readable summary.
      const messages = detail
        .map((item: unknown) => (typeof item === 'object' && item && 'msg' in item ? String((item as { msg: unknown }).msg) : null))
        .filter((m): m is string => !!m);
      if (messages.length > 0) return messages.join('; ');
    }
    if (detail && typeof detail === 'object') {
      return JSON.stringify(detail);
    }
  } catch {
    // not JSON — fall through to the fallback
  }
  return fallback;
};

export const listMetaAdAccounts = async (sync = false): Promise<MetaAdAccountInfo[]> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/ad-accounts${sync ? '?sync=true' : ''}`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const selectMetaAdAccount = async (metaAdAccountId: number): Promise<MetaIntegrationStatus> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/ad-accounts/select`, {
    method: 'POST',
    body: JSON.stringify({ meta_ad_account_id: metaAdAccountId }),
  });
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

/**
 * List the Facebook Pages the user can admin for an ad account (#4277 chunk 3).
 *
 * Backed by Graph /me/accounts via the connected integration's access token. The user's selection
 * persists per-MetaAdAccount via `selectMetaPageForAdAccount`.
 */
export const listMetaPagesForAdAccount = async (metaAdAccountId: number): Promise<MetaPageInfo[]> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/meta/ad-accounts/${metaAdAccountId}/pages`,
  );
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

/** Bind a Facebook Page to an ad account (#4277 chunk 3). Returns the updated integration status. */
export const selectMetaPageForAdAccount = async (
  data: MetaPageSelectRequest,
): Promise<MetaIntegrationStatus> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/ad-accounts/select-page`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

/**
 * List the Meta Pixels on an ad account (Phase 7 Chunk 0).
 *
 * Backed by Graph /act_{id}/adspixels via the connected integration's access token. Pixels are
 * ad-account-scoped (unlike Pages). The user's selection persists via `selectMetaPixelForAdAccount`.
 */
export const listMetaPixelsForAdAccount = async (metaAdAccountId: number): Promise<MetaPixelInfo[]> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/meta/ad-accounts/${metaAdAccountId}/pixels`,
  );
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

/** The PUBLISHED instant forms on the ad account's bound Facebook Page (#6186) — the wizard's form picker. */
export const listMetaLeadgenFormsForAdAccount = async (metaAdAccountId: number): Promise<MetaLeadgenFormInfo[]> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/ad-accounts/${metaAdAccountId}/leadgen-forms`);
  await assertOk(response);
  return response.json();
};

/** Bind a Meta Pixel to an ad account (Phase 7 Chunk 0). Returns the updated integration status. */
export const selectMetaPixelForAdAccount = async (
  data: MetaPixelSelectRequest,
): Promise<MetaIntegrationStatus> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/ad-accounts/select-pixel`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const listMetaCampaigns = async (offerUrl?: string): Promise<MetaCampaignResponse[]> => {
  const url = offerUrl
    ? `${apiEndpoint}/meta/campaigns?offer_url=${encodeURIComponent(offerUrl)}`
    : `${apiEndpoint}/meta/campaigns`;
  const response = await fetchWithAuth(url);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const getMetaCampaign = async (slug: string): Promise<MetaCampaignDetailResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

// Phase 7 (#3824) snapshot-backed dashboards. The first four read the org's selected Meta ad account;
// CAPI health is org-scoped. Each returns the reporting window alongside the rows (except CAPI health).
export const getMetaCampaignOverview = async (): Promise<MetaCampaignOverviewResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/dashboards/campaign-overview`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const getMetaCreativePerformance = async (): Promise<MetaCreativePerformanceResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/dashboards/creative-performance`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const getMetaAdSetPerformance = async (): Promise<MetaAdSetPerformanceResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/dashboards/adset-performance`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const getMetaBudgetPacing = async (): Promise<MetaBudgetPacingResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/dashboards/budget-pacing`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

// Per-day Meta rollups over [startDate, endDate] for the consolidated Performance chart (CP-4/CP-6).
export const getMetaTimeSeries = async (
  startDate: string,
  endDate: string,
  signal?: AbortSignal,
): Promise<MetaTimeSeriesResponse> => {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  const response = await fetchWithAuth(`${apiEndpoint}/meta/dashboards/time-series?${params.toString()}`, { signal });
  await assertOk(response, 'Failed to fetch Meta time series');
  return response.json();
};

// Per-placement (publisher_platform) metrics — a live account-level breakdown (#6049). Optional date window.
export const getMetaPlacement = async (
  startDate?: string,
  endDate?: string,
): Promise<MetaPlacementResponse> => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const qs = params.toString();
  const response = await fetchWithAuth(`${apiEndpoint}/meta/dashboards/placement${qs ? `?${qs}` : ''}`);
  await assertOk(response, 'Failed to fetch Meta placement');
  return response.json();
};

export const updateMetaCampaignBudget = async (
  campaignId: number,
  body: BudgetUpdateRequest,
): Promise<MetaCampaignBudgetResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/budgets/meta/campaigns/${campaignId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const updateMetaAdSetBudget = async (
  adSetId: number,
  body: BudgetUpdateRequest,
): Promise<MetaAdSetBudgetResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/budgets/meta/ad-sets/${adSetId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const updateMetaCampaignStatus = async (
  slug: string,
  body: MetaCampaignStatusRequest,
): Promise<MetaCampaignStatusResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/status`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

/** Soft-delete a Meta campaign (#6179): ARCHIVED on Meta + locally, cascading to its ad sets and ads. */
export const deleteMetaCampaign = async (slug: string): Promise<MetaCampaignStatusResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}`, { method: 'DELETE' });
  await assertOk(response);
  return response.json();
};

/** The org's HubSpot→Meta CAPI conversion mappings + linkable stages (#5981 Conversions tab). */
export const getMetaConversions = async (): Promise<MetaConversionsResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/conversions`);
  await assertOk(response);
  return response.json();
};

/** Link a HubSpot stage mapping to Meta — its conversions start uploading via CAPI (#5981). */
export const linkMetaConversion = async (stageMappingId: number): Promise<MetaConversionMappingRow> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/conversions/${stageMappingId}`, { method: 'POST' });
  await assertOk(response);
  return response.json();
};

/** Stop a stage mapping's Meta uploads (#5981) — deactivates, keeping upload history. */
export const unlinkMetaConversion = async (stageMappingId: number): Promise<MetaConversionMappingRow> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/conversions/${stageMappingId}`, { method: 'DELETE' });
  await assertOk(response);
  return response.json();
};

/**
 * Update a Meta campaign's bidding from the Budget tab's Bidding panel (#5981) — strategy switches
 * push to Meta; a target-CPL change is local only. Returns the refreshed detail tree.
 */
export const updateMetaCampaignBidding = async (
  slug: string,
  body: MetaCampaignBiddingUpdateRequest,
): Promise<MetaCampaignDetailResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/bidding`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

/**
 * Daily-budget recommendation for a Meta campaign (#5981) — grounded in Meta's learning-phase
 * guidance ((50 events/week / 7) x CPL); 400 with an actionable message when no CPL exists yet.
 */
export const getMetaBudgetRecommendation = async (slug: string): Promise<MetaBudgetRecommendationResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/budget-recommendation`);
  await assertOk(response);
  return response.json();
};

/** The optimization-event catalog: standard Pixel events + the account's custom conversions (#6076). */
export const getMetaConversionEvents = async (outcome?: MetaCampaignOutcome): Promise<MetaConversionEventOption[]> => {
  // Standard events are objective-scoped on Meta (#6186): passing the outcome narrows the list to
  // events the ad set can actually publish with (a LEAD pick under Sales fails with subcode 2446814).
  const query = outcome ? `?outcome=${outcome}` : '';
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/conversion-events${query}`);
  await assertOk(response);
  return response.json();
};

/**
 * Update what a Meta campaign's ad sets optimize toward and/or their attribution windows (#6076).
 * Conversion-objective campaigns only; returns the refreshed detail tree.
 */
export const updateMetaConversionSettings = async (
  slug: string,
  body: MetaConversionSettingsUpdateRequest,
): Promise<MetaCampaignDetailResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/conversion-settings`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

/** Search Meta's interest entities for the Targeting tab's Interests picker (#6076). */
export const searchMetaInterests = async (query: string): Promise<MetaInterestEntry[]> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/meta/campaigns/targeting/interests?q=${encodeURIComponent(query)}`,
  );
  await assertOk(response);
  return response.json();
};

/** The selected ad account's saved custom audiences — the Audience picker's catalog (#6076). */
export const getMetaCustomAudiences = async (): Promise<MetaCustomAudienceRef[]> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/targeting/custom-audiences`);
  await assertOk(response);
  return response.json();
};

/** Search Meta's language locales for the Targeting tab's Languages picker (#5981). */
export const searchMetaLocales = async (query: string): Promise<MetaLocaleEntry[]> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/meta/campaigns/targeting/locales?q=${encodeURIComponent(query)}`,
  );
  await assertOk(response);
  return response.json();
};

/**
 * Update a Meta campaign's geo targeting from the detail page's Targeting tab (#5981). Partial
 * update: each provided field replaces its slice of the spec (positive selection, exclusions,
 * languages); omitted fields stay untouched. Returns the refreshed detail tree.
 */
/** Meta's own rendering of ONE ad (#6213) — the eye icon's preview iframe snippet. */
export const getMetaAdPreview = async (adId: number, adFormat: string): Promise<MetaAdPreviewResponse> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/meta/campaigns/ads/${adId}/preview?ad_format=${encodeURIComponent(adFormat)}`,
  );
  await assertOk(response);
  return response.json();
};

/** Add ONE new ad set to a pushed Meta campaign (#6211) — async; poll the task status. */
export const createMetaAdSet = async (
  slug: string,
  body: MetaAdSetCreateRequest,
): Promise<MetaAdSetDispatchResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/ad-sets`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

/** Status of the most recent add-ad-set task for a campaign (#6211); null if never run. */
export const getMetaAdSetTaskStatus = async (slug: string): Promise<MetaCampaignTaskStatusResponse | null> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/ad-set-task-status`);
  await assertOk(response);
  return response.json();
};

/** Switch a Meta campaign between ABO and CBO (#6211). Meta rate-limits the toggle (~90 min). */
export const updateMetaCampaignBudgetMode = async (
  slug: string,
  body: MetaBudgetModeUpdateRequest,
): Promise<MetaCampaignDetailResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/budget-mode`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

export const updateMetaCampaignTargeting = async (
  slug: string,
  body: MetaCampaignTargetingUpdateRequest,
): Promise<MetaCampaignDetailResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/targeting`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

/**
 * Edit a Meta ad's copy inline from the campaign screen (#5981). Meta creatives are immutable, so
 * the backend rebuilds the creative with the merged copy and swaps it onto the ad; editing an
 * ACTIVE ad re-enters Meta review.
 */
export const updateMetaAdCopy = async (adId: number, body: MetaAdCopyUpdateRequest): Promise<MetaAdResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/ads/${adId}/copy`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

/** Rename ONE ad set (#6210) — the ad-set surface's editable name, Google's ad-group-name analog. */
export const updateMetaAdSetName = async (adSetId: number, name: string): Promise<MetaAdSetResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/ad-sets/${adSetId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
  await assertOk(response);
  return response.json();
};

/** Pause/resume ONE ad (#6179) — the Google per-ad state toggle. ACTIVE|PAUSED only. */
export const updateMetaAdStatus = async (adId: number, body: MetaAdStatusUpdateRequest): Promise<MetaAdResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/ads/${adId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  await assertOk(response);
  return response.json();
};

/** Soft-delete ONE ad (#6179): ARCHIVED on Meta + locally; it leaves the campaign detail. One-way. */
export const deleteMetaAd = async (adId: number): Promise<MetaAdResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/ads/${adId}`, { method: 'DELETE' });
  await assertOk(response);
  return response.json();
};

/**
 * Regenerate a Meta ad's copy from its campaign's offer URL (#5981) — one click, one action. The
 * backend runs a fresh LLM pass and applies all three fields through the same rebuild-and-swap
 * path as `updateMetaAdCopy`.
 */
export const regenerateMetaAdCopy = async (adId: number): Promise<MetaAdResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/ads/${adId}/regenerate-copy`, {
    method: 'POST',
    // LLM scrape + generation + creative rebuild routinely lands near the 30s default (a 30.3s
    // staging request completed server-side while the client had already aborted — Jeff's #6214
    // "regenerate errors" report). Same per-call override the other LLM-backed endpoints use.
    timeoutMs: 90000,
  });
  await assertOk(response);
  return response.json();
};

/**
 * Fetch per-resource progress for an in-flight Meta campaign (#4288 Phase 4). Backs the
 * "Generating Campaign" card on the Meta detail panel. The caller polls this while
 * `state === 'IN_PROGRESS'` and stops once it flips to `COMPLETED` / `FAILED`.
 */
export const getMetaCampaignProgress = async (
  slug: string,
): Promise<MetaCampaignGenerationProgress> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns/${slug}/progress`);
  if (!response.ok) throw new Error(await safeErrorDetail(response, `Request failed: ${response.status}`));
  return response.json();
};

export const createMetaPhaseCampaign = async (
  data: MetaPhaseCampaignRequest
): Promise<MetaCampaignDispatchResponse> => {
  const response = await fetchWithAuth(`${apiEndpoint}/meta/campaigns`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const detail = await safeErrorDetail(response, `Request failed: ${response.status}`);
    // 409 = a creation for this offer is already running (#5742 M-2; the duplicate-submission guard). The
    // backend's detail is already specific and user-facing; surface it as a conflict the wizard can
    // render inline instead of collapsing it into the generic destructive error toast.
    if (response.status === 409) throw new MetaCampaignConflictError(detail);
    throw new Error(detail);
  }
  return response.json();
};

export const getMetaCampaignTaskStatus = async (
  offerUrl: string
): Promise<MetaCampaignTaskStatusResponse | null> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/meta/campaigns/task-status?offer_url=${encodeURIComponent(offerUrl)}`
  );
  if (!response.ok) throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  return response.json(); // the backend returns null when there has never been a creation task for this offer
};

// Alias for component compatibility
export const getConversionActions = listConversionActions;

export const getCampaignConversionActions = async (campaignId: number): Promise<any[]> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/conversion-actions`);
  await assertOk(response);
  return response.json();
};

export const linkCampaignConversionAction = async (
  campaignId: number,
  conversionActionId: number,
  isPrimary: boolean = false
): Promise<{ success: boolean; id: number }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/conversion-actions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversion_action_id: conversionActionId, is_primary: isPrimary }),
  });
  await assertOk(response);
  return response.json();
};

export const unlinkCampaignConversionAction = async (
  campaignId: number,
  conversionActionId: number
): Promise<{ success: boolean }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/conversion-actions/${conversionActionId}`,
    { method: 'DELETE' }
  );
  await assertOk(response);
  return response.json();
};

export const getCurrentUserProfile = async (): Promise<UserProfile> => {
  // Check if we have a token before attempting to fetch
  const token = getCookie('access_token');
  if (!token) {
    // Throw the same error structure that fetchWithAuth would throw on 401
    // This prevents making an HTTP request when we know it will fail
    throw { to: '/signin', isRedirect: true, statusCode: 307, headers: {}, reloadDocument: false };
  }

  const response = await fetchWithAuth(`${apiEndpoint}/user/me`);
  await assertOk(response);
  return response.json();
};

export async function acceptOrganizationInvitation(
  organizationId: number,
): Promise<{ message: string; organization_id: number }> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/accept-invitation`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to accept invitation');
  }

  const result = await response.json();
  return result;
}

export async function rejectOrganizationInvitation(
  organizationId: number,
): Promise<{ message: string; organization_id: number }> {
  const response = await fetchWithAuth(`${apiEndpoint}/organizations/${organizationId}/reject-invitation`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Failed to reject invitation');
  }

  const result = await response.json();
  return result;
}

export async function fetchGoogleAdsAccountUsers(): Promise<GoogleAdsAccountUser[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/google/accounts/users`, {
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Google Ads account users: ${response.statusText}`);
  }
  return response.json();
}

// Add location-specific types
export interface LocationRecommendation {
  id: number;
  type: 'ALTERNATE_LOCATION';
  recommendation_metadata: {
    location_name: string;
    location_id: string;
    base_campaign_id: string;
    base_campaign_name: string;
    expected_impact: {
      impressions: number;
      clicks: number;
      conversions: number;
      cost: number;
    };
  };
  reason: string;
  priority: 'high' | 'medium' | 'low';
  created_at: string;
}

// Add function to get location recommendations
export async function getLocationRecommendations(organizationId: string): Promise<LocationRecommendation[]> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/admin/organizations/${organizationId}/recommendations?type=ALTERNATE_LOCATION`,
  );
  if (!response.ok) {
    throw new Error('Failed to fetch location recommendations');
  }
  return response.json();
}

// Add function to apply location recommendation
export async function applyLocationRecommendation(
  recommendationId: number,
  baseCampaignId?: string,
): Promise<{ success: boolean; campaign_id: string; message: string }> {
  const response = await fetchWithAuth(`${apiEndpoint}/admin/recommendations/${recommendationId}/apply`, {
    method: 'POST',
    body: baseCampaignId ? JSON.stringify({ base_campaign_id: baseCampaignId }) : undefined,
  });
  if (!response.ok) {
    throw new Error('Failed to apply location recommendation');
  }
  return response.json();
}

// Add function to create location recommendation
export async function createLocationRecommendation(
  organizationId: string,
  baseCampaignId: string,
  locationData?: {
    location_id: string;
    location_name: string;
  },
): Promise<LocationRecommendation> {
  const response = await fetchWithAuth(`${apiEndpoint}/admin/organizations/${organizationId}/recommendations`, {
    method: 'POST',
    body: JSON.stringify({
      base_campaign_id: baseCampaignId,
      ...(locationData && { location_data: locationData }),
    }),
  });
  if (!response.ok) {
    throw new Error('Failed to create location recommendation');
  }
  return response.json();
}

// Process recommendations at organization level
export async function processOrganizationRecommendations(
  actionType: string,
  organizationId: string | number,
): Promise<ProcessRecommendationsResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/recommendations/process`, {
    method: 'POST',
    body: JSON.stringify({
      action_type: actionType,
      organisation_id: organizationId,
    }),
  });
  if (!response.ok) {
    throw new Error('Failed to process organization recommendations');
  }
  return response.json();
}

// Keyword Metrics API functions
export async function getKeywordMetrics(keyword: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/keywords/${encodeURIComponent(keyword)}/metrics`);
  if (!response.ok) {
    throw new Error('Failed to fetch keyword metrics');
  }
  return response.json();
}

export async function getKeywordMetricsById(keywordId: number, campaignId?: number) {
  const url = new URL(`${apiEndpoint}/keywords/${keywordId}/metrics`);
  if (campaignId) {
    url.searchParams.set('campaign_id', campaignId.toString());
  }

  const response = await fetchWithAuth(url.toString());
  if (!response.ok) {
    throw new Error('Failed to fetch keyword metrics by ID');
  }
  return response.json();
}

export interface BatchKeywordMetrics {
  keyword_id: number;
  keyword_text: string;
  avg_monthly_searches: number | null;
  competition: string | null;
  competition_index: number | null;
  low_top_of_page_bid: number | null;
  high_top_of_page_bid: number | null;
  last_updated: string | null;
  metrics_available: boolean;
}

export async function getKeywordMetricsBatch(keywordIds: number[]): Promise<BatchKeywordMetrics[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/keywords/metrics/batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(keywordIds),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch batch keyword metrics');
  }
  return response.json();
}

export async function getKeywordAIAnalysis(
  keywordText: string,
  campaignId: number,
  options?: { url?: string; includeAlternatives?: boolean },
) {
  const url = new URL(`${apiEndpoint}/keywords/${encodeURIComponent(keywordText)}/ai-analysis`);
  url.searchParams.set('campaign_id', campaignId.toString());

  if (options?.url) {
    url.searchParams.set('url', options.url);
  }
  if (options?.includeAlternatives !== undefined) {
    url.searchParams.set('include_alternatives', options.includeAlternatives.toString());
  }

  const response = await fetchWithAuth(url.toString());
  if (!response.ok) {
    throw new Error('Failed to fetch keyword AI analysis');
  }
  return response.json();
}

// Admin: Backfill keyword metrics for a campaign
export async function backfillCampaignKeywordMetrics(campaignId: number): Promise<BackfillTaskResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/admin/campaigns/${campaignId}/keywords/metrics/backfill`, {
    method: 'POST',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to start keyword metrics backfill');
  }
  return response.json();
}


export async function generateHeadline(
  campaignId: number,
  adId: number,
  keywordId: number,
  funnelStageId?: number,
): Promise<any> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/headlines/generate`, {
    method: 'POST',
    body: JSON.stringify({ keyword_id: keywordId, funnel_stage_id: funnelStageId }),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to generate headline');
  }
  return response.json();
}

export async function generateAllHeadlines(
  campaignId: number,
  adId: number,
  funnelStage: string,
  count?: number,
): Promise<any> {
  const body: any = { funnel_stage: funnelStage };
  if (count !== undefined) {
    body.count = count;
  }
  
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/headlines/generate-batch`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to generate headlines');
  }
  return response.json();
}


export async function generateAllDescriptions(
  campaignId: number,
  adId: number,
  funnelStage: string,
  count?: number,
): Promise<any> {
  const body: any = { funnel_stage: funnelStage };
  if (count !== undefined) {
    body.count = count;
  }

  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/descriptions/generate-batch`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to generate descriptions');
  }
  return response.json();
}


/**
 * Delete the current user's account (soft delete).
 * This will pause all campaigns if the user is the last member of an organization.
 */
export async function deleteUserAccount(): Promise<AccountDeletionResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/user/me`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorRes = await response.json();
    throw new Error(errorRes.detail || 'Failed to delete account');
  }
  return response.json();
}

/**
 * Get user's notification email settings.
 */
export async function getUserNotificationSettings(): Promise<UserNotificationSettings> {
  const response = await fetchWithAuth(`${apiEndpoint}/user/notification-settings`);
  if (!response.ok) {
    throw new Error('Failed to fetch notification settings');
  }
  return response.json();
}

/**
 * Update user's notification email settings.
 */
export async function updateUserNotificationSettings(
  settings: UserNotificationSettingsUpdate,
): Promise<UserNotificationSettings> {
  const response = await fetchWithAuth(`${apiEndpoint}/user/notification-settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error('Failed to update notification settings');
  }
  return response.json();
}

export async function getUserSettings(): Promise<UserSettings> {
  const response = await fetchWithAuth(`${apiEndpoint}/user/settings`);
  if (!response.ok) {
    throw new Error('Failed to fetch user settings');
  }
  return response.json();
}

export async function updateUserSettings(settings: UserSettingsUpdate): Promise<UserSettings> {
  const response = await fetchWithAuth(`${apiEndpoint}/user/settings`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error('Failed to update user settings');
  }
  return response.json();
}

// Optimisation Config
export async function getOptimisationConfig(organisationId: string): Promise<OptimisationConfig> {
  const response = await fetchWithAuth(`${apiEndpoint}/optimisations/config/${organisationId}`);
  if (!response.ok) throw new Error('Failed to fetch optimisation config');
  return response.json();
}

export async function updateOptimisationConfig(
  organisationId: string,
  config: OptimisationConfigUpdate,
): Promise<OptimisationConfig> {
  const response = await fetchWithAuth(`${apiEndpoint}/optimisations/config/${organisationId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!response.ok) throw new Error('Failed to update optimisation config');
  return response.json();
}

// Optimisation History
export async function getOptimisationHistory(
  organisationId: string,
  skip?: number,
  limit?: number,
): Promise<OptimisationHistoryResponse> {
  const params = new URLSearchParams();
  if (skip) params.append('skip', skip.toString());
  if (limit) params.append('limit', limit.toString());

  const response = await fetchWithAuth(`${apiEndpoint}/optimisations/${organisationId}/history?${params}`);
  if (!response.ok) throw new Error('Failed to fetch optimisation history');
  return response.json();
}

// Optimisation Opportunities
export async function getOptimisationOpportunities(organisationId: string): Promise<OptimisationOpportunity[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/optimisations/${organisationId}/opportunities`);
  if (!response.ok) throw new Error('Failed to fetch opportunities');
  return response.json();
}

export async function approveOptimisation(recommendationId: string): Promise<ActionLog> {
  const response = await fetchWithAuth(`${apiEndpoint}/optimisations/recommendations/${recommendationId}/approve`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error('Failed to approve optimisation');
  return response.json();
}

export async function dismissOptimisation(recommendationId: string, reason?: string): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/optimisations/recommendations/${recommendationId}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) throw new Error('Failed to dismiss optimisation');
}

export async function archiveCampaign(campaignId: number): Promise<{ message: string; campaign_id: number; budget_freed: number }> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/archive`, { method: 'PUT' });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to archive campaign');
  }
  return response.json();
}

export async function restoreCampaign(campaignId: number): Promise<{ message: string; campaign_id: number }> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/restore`, { method: 'PUT' });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to restore campaign');
  }
  return response.json();
}

export async function dismissFailedReportTask(businessId: number, taskTrackingId: number): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/reports/business/${businessId}/tasks/${taskTrackingId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to dismiss failed report');
}

/**
 * Get list of competitors for a business
 */
export async function getCompetitors(businessId: number): Promise<any[]> {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors/business/${businessId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch competitors');
  }
  return response.json();
}

/**
 * Get a single competitor by ID
 */
export async function getCompetitor(competitorId: string): Promise<any> {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors/${competitorId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch competitor');
  }
  return response.json();
}

export async function deleteCompetitor(competitorId: string) {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors/${competitorId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to remove competitor');
  }
}

// #4792: PUT /competitors/{id} — used to make the Proximity (closeness) rating user-editable.
export async function updateCompetitor(
  competitorId: string,
  payload: { competitor_closeness?: number },
): Promise<unknown> {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors/${competitorId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error('Failed to update competitor');
  }
  return response.json();
}

// Types for competitor creation
export interface CompetitorTaskResult {
  url: string;
  task_tracking_id: number;
  celery_task_id: string;
  competitor_id: string;
}

export interface CompetitorSkippedResult {
  url: string;
  reason: string;
  task_tracking_id?: number;
  competitor_id?: string;
}

export interface CompetitorValidationError {
  url: string;
  error: string;
}

export interface CreateCompetitorsResponse {
  message: string;
  business_id: number;
  tasks: CompetitorTaskResult[];
  skipped: CompetitorSkippedResult[];
  total_queued: number;
  total_skipped: number;
  validation_errors?: CompetitorValidationError[];
}

export interface CompetitorTaskStatus {
  task_tracking_id: number;
  state: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  url: string | null;
  created_at: string | null;
  updated_at: string | null;
  error?: string;
}

export interface CompetitorTasksResponse {
  business_id: number;
  tasks: CompetitorTaskStatus[];
  has_in_progress: boolean;
  summary: {
    total: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
}

/**
 * Create competitors from URLs (triggers AI extraction)
 * Max 10 URLs per request for rate limiting
 */
export async function createCompetitors(
  businessId: number,
  competitorUrls: string[]
): Promise<CreateCompetitorsResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      business_id: businessId,
      competitor_urls: competitorUrls,
    }),
  });

  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to create competitors');
  }

  return response.json();
}

/**
 * Get competitor task status by business ID (for polling)
 */
export async function getCompetitorTasksByBusiness(
  businessId: number
): Promise<CompetitorTasksResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors/business/${businessId}/tasks`);

  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to get competitor tasks');
  }

  return response.json();
}

/**
 * Response type for report task status polling
 */
export interface ReportTasksResponse {
  business_id: number;
  tasks: Array<{
    task_tracking_id: number;
    state: string;
    task_type: string;
    url: string | null;
    created_at: string | null;
    updated_at: string | null;
    error?: string;
  }>;
  has_in_progress: boolean;
  summary: {
    total: number;
    in_progress: number;
    completed: number;
    failed: number;
  };
}

/**
 * Get report task status by business ID (for polling)
 */
export async function getReportTasksByBusiness(
  businessId: number
): Promise<ReportTasksResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/reports/business/${businessId}/tasks`);

  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to get report tasks');
  }

  return response.json();
}

/**
 * Generate a competitor analysis report
 * Triggers an AI-powered report generation using web search
 */
export interface GenerateCompetitorReportRequest {
  organization_id: number;
  googleadsaccount_id?: number;
  business_id?: number;
}

export interface GenerateCompetitorReportResponse {
  task_id: number;
  status: string;
  message: string;
  competitor_id: string;
  competitor_name: string;
}

export async function generateCompetitorReport(
  competitorId: string,
  request: GenerateCompetitorReportRequest
): Promise<GenerateCompetitorReportResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/competitors/${competitorId}/generate-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to generate competitor report');
  }

  return response.json();
}

// Optimization review types and API
export interface ConfirmOptimizationAction {
  action_id: number;
  manual_tag: 'Approve' | 'Undo' | 'Reject';
  reason: 'brand_variants' | 'prospecting_terms' | 'competitors' | '';
  notes: string;
}

export interface ConfirmOptimizationsRequest {
  actions: ConfirmOptimizationAction[];
}

export interface ConfirmOptimizationsResponse {
  success: boolean;
  confirmed: number;
  errors: string[];
}

export async function confirmOptimizations(
  request: ConfirmOptimizationsRequest
): Promise<ConfirmOptimizationsResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/optimizations/confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to confirm optimizations');
  }

  return response.json();
}

// Search Terms Report (RMF R.70 compliance)
export interface SearchTermMetrics {
  search_term: string;
  match_type: string; // EXACT, PHRASE, BROAD
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  cvr: number;
  campaign_id: number;
  campaign_name: string;
  ad_group_id: string;
  ad_group_name: string;
  most_recent_date: string;
}

export interface SearchTermsReportResponse {
  search_terms: SearchTermMetrics[];
  count: number;
  days: number;
}

export async function fetchSearchTermsReport(
  days: number = 30,
  organizationId?: number,
  signal?: AbortSignal
): Promise<SearchTermsReportResponse> {
  const params = new URLSearchParams();
  params.append('days', days.toString());
  if (organizationId) {
    params.append('organization_id', organizationId.toString());
  }

  const endpoint = `${apiEndpoint}/google/reports/search-terms?${params.toString()}`;

  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to fetch search terms report');
  }

  return response.json();
}

export async function getCampaignHeadlineMetrics(
  campaignId: number,
  days: number = 30,
): Promise<CampaignHeadlineMetricsResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/headline-metrics?days=${days}`,
  );
  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to fetch headline metrics');
  }
  return response.json();
}

export async function getCampaignDescriptionMetrics(
  campaignId: number,
  days: number = 30,
): Promise<CampaignDescriptionMetricsResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/description-metrics?days=${days}`,
  );
  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to fetch description metrics');
  }
  return response.json();
}

// API Keys
export async function listApiKeys(organizationId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/api-keys/organizations/${organizationId}/keys`,
  );
  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to fetch API keys');
  }
  return response.json();
}

export async function createApiKey(organizationId: number, name: string) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/api-keys/organizations/${organizationId}/keys`,
    {
      method: 'POST',
      body: JSON.stringify({ name }),
    },
  );
  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to create API key');
  }
  return response.json();
}

export async function revokeApiKey(organizationId: number, keyId: number) {
  const response = await fetchWithAuth(
    `${apiEndpoint}/api-keys/organizations/${organizationId}/keys/${keyId}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    const errorRes = await response.json().catch(() => ({}));
    throw new Error(errorRes.detail || 'Failed to revoke API key');
  }
  return response.json();
}

export const fetchExternalCampaigns = async (
  accountId: string,
  refresh = false,
): Promise<ExternalCampaignSummary[]> => {
  const params = refresh ? '?refresh=true' : '';
  const response = await fetchWithAuth(
    `${apiEndpoint}/google/accounts/${accountId}/external-campaigns${params}`,
  );
  if (!response.ok) {
    throw new Error('Failed to fetch external campaigns');
  }
  return response.json();
};

export const fetchExternalCampaignDetail = async (
  accountId: string,
  campaignId: string,
): Promise<ExternalCampaignDetail> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/google/accounts/${accountId}/external-campaigns/${campaignId}`,
  );
  if (!response.ok) {
    throw new Error('Failed to fetch external campaign detail');
  }
  return response.json();
};

/* Cross-platform creative-asset library (#4277).
 *
 * The org's library is read via GET /assets (paginated, optionally filtered by platform / source),
 * uploaded via multipart POST /assets/upload (single image) or POST /assets/upload/zip (archive),
 * and soft-deleted via DELETE /assets/{id}. Responses carry a short-lived signed_url minted per
 * read — re-fetch each time, never cache.
 */

export const listCreativeAssets = async (
  params: CreativeAssetListParams = {},
): Promise<CreativeAssetListResponse> => {
  const query = new URLSearchParams();
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  if (params.platform) query.set('platform', params.platform);
  if (params.source) query.set('source', params.source);
  if (params.search) query.set('search', params.search);
  // Repeated `tags` params (?tags=a&tags=b) — FastAPI parses these into a list (#5383).
  params.tags?.forEach((t) => query.append('tags', t));
  const qs = query.toString();
  const url = qs ? `${apiEndpoint}/assets?${qs}` : `${apiEndpoint}/assets`;
  const response = await fetchWithAuth(url);
  if (!response.ok) throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const getCreativeAsset = async (assetId: number): Promise<CreativeAsset> => {
  const response = await fetchWithAuth(`${apiEndpoint}/assets/${assetId}`);
  if (!response.ok) throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const uploadCreativeAsset = async (file: File): Promise<CreativeAsset> => {
  const formData = new FormData();
  formData.append('file', file);
  // ``fetchWithAuth`` adds the auth header; the browser sets the multipart ``Content-Type``
  // boundary automatically when we pass ``FormData`` as the body — never set it manually.
  // Uploads can legitimately exceed the default 30s on slow links — allow 2 minutes (#4997).
  const response = await fetchWithAuth(`${apiEndpoint}/assets/upload`, {
    method: 'POST',
    body: formData,
    timeoutMs: 120000,
  });
  if (!response.ok) {
    // ``safeErrorDetail`` normalises FastAPI's array/object ``detail`` shapes to a string so
    // the UI never renders ``[object Object]`` — user-reported regression against the asset
    // picker on the Meta wizard's Pick Assets step.
    throw new Error(await safeErrorDetail(response, `Upload failed: ${response.status} ${response.statusText}`));
  }
  return response.json();
};

export const uploadCreativeAssetZip = async (file: File): Promise<CreativeAssetZipUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  // Zip uploads carry multiple assets — allow 2 minutes like the single-asset upload (#4997).
  const response = await fetchWithAuth(`${apiEndpoint}/assets/upload/zip`, {
    method: 'POST',
    body: formData,
    timeoutMs: 120000,
  });
  if (!response.ok) {
    throw new Error(await safeErrorDetail(response, `Upload failed: ${response.status} ${response.statusText}`));
  }
  return response.json();
};

export const deleteCreativeAsset = async (assetId: number): Promise<void> => {
  const response = await fetchWithAuth(`${apiEndpoint}/assets/${assetId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
};

export const generateCreativeAssets = async (params: CreativeAssetGenerateParams): Promise<CreativeAsset[]> => {
  // gpt-image-1 takes 30-90s per image (requests run in parallel server-side) — allow 5 minutes.
  const response = await fetchWithAuth(`${apiEndpoint}/assets/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    timeoutMs: 300000,
  });
  await assertOk(response, 'Image generation failed');
  return response.json();
};

export const addCreativeAssetTag = async (
  assetId: number,
  tagValue: string,
  tagType: 'system' | 'user' = 'user',
): Promise<CreativeAssetTag> => {
  const response = await fetchWithAuth(`${apiEndpoint}/assets/${assetId}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag_value: tagValue, tag_type: tagType }),
  });
  if (!response.ok) throw new Error(`Tag add failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const removeCreativeAssetTag = async (assetId: number, tagValue: string): Promise<void> => {
  const response = await fetchWithAuth(`${apiEndpoint}/assets/${assetId}/tags/${encodeURIComponent(tagValue)}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(`Tag remove failed: ${response.status} ${response.statusText}`);
};

/* Primary-logo designation (#4776). The backend derives the slot (square 1:1 vs landscape 4:1)
 * from the asset's dimensions and moves it off any previous holder, so each org holds at most
 * one of each. 422 details (wrong ratio, mime, or file size) are user-facing — surface them.
 */

export const setCreativeAssetPrimaryLogo = async (assetId: number): Promise<CreativeAsset> => {
  const response = await fetchWithAuth(`${apiEndpoint}/assets/${assetId}/primary-logo`, {
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error(await safeErrorDetail(response, `Request failed: ${response.status} ${response.statusText}`));
  }
  return response.json();
};

export const unsetCreativeAssetPrimaryLogo = async (assetId: number): Promise<void> => {
  const response = await fetchWithAuth(`${apiEndpoint}/assets/${assetId}/primary-logo`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(await safeErrorDetail(response, `Request failed: ${response.status} ${response.statusText}`));
  }
};


// ---- BusinessLocation ----------------------------------------------------

export async function fetchBusinessLocations(
  businessId: number,
  includeDismissed = false,
): Promise<BusinessLocationRead[]> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations?include_dismissed=${includeDismissed}`,
  );
  if (!response.ok) throw new Error('Failed to fetch business locations');
  return response.json();
}

export async function createBusinessLocation(
  businessId: number,
  payload: BusinessLocationCreate,
): Promise<BusinessLocationRead> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to create location');
  }
  return response.json();
}

export async function updateBusinessLocation(
  businessId: number,
  locationUuid: string,
  patch: BusinessLocationUpdate,
): Promise<BusinessLocationRead> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/${locationUuid}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to update location');
  }
  return response.json();
}

export async function dismissBusinessLocation(
  businessId: number,
  locationUuid: string,
): Promise<void> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/${locationUuid}`,
    { method: 'DELETE' },
  );
  if (!response.ok && response.status !== 204) {
    // #4816: surface the backend's reason (e.g. "targeted by campaign X") instead of a generic error.
    const detail = await response.json().then((d) => d?.detail).catch(() => null);
    throw new Error(typeof detail === 'string' ? detail : 'Failed to dismiss location');
  }
}

export async function resetBusinessLocationToAgent(
  businessId: number,
  locationUuid: string,
): Promise<BusinessLocationRead> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/${locationUuid}/reset-to-agent`,
    { method: 'POST' },
  );
  if (!response.ok) throw new Error('Failed to reset location to agent');
  return response.json();
}

export async function restoreBusinessLocation(
  businessId: number,
  locationUuid: string,
): Promise<BusinessLocationRead> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/${locationUuid}/restore`,
    { method: 'POST' },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to restore location');
  }
  return response.json();
}

/** Staged "Save Changes" save: replace the business's active location set with
 * `locations` in one atomic call (PUT). Backs the batched location save so edits
 * aren't persisted per-change. */
export async function reconcileBusinessLocations(
  businessId: number,
  locations: BusinessLocationReconcileItem[],
): Promise<BusinessLocationRead[]> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations`,
    { method: 'PUT', body: JSON.stringify({ locations }) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to save locations');
  }
  return response.json();
}

export interface BusinessAtomicSavePayload {
  /** Core business fields (same shape as the PATCH /business body). */
  business?: BusinessUpdateData;
  /** KPI targets to upsert for the connected Ads account. */
  kpi_targets?: AccountKpiTargetsUpsert;
  /** Full desired active location set to reconcile. */
  locations?: BusinessLocationReconcileItem[];
}

export interface BusinessAtomicSaveResponse {
  business: Business;
  kpi_targets?: AccountKpiTargetsWire;
  locations?: BusinessLocationRead[];
}

/**
 * Atomic business "Save Changes" (#5401): core fields + KPI targets + locations in ONE
 * transaction that fully succeeds or fully rolls back. Any subset may be present. Budget is saved
 * separately by design — a live-campaign budget syncs to Google Ads, which can't join a DB
 * transaction.
 */
export async function saveBusinessAtomic(
  businessId: number,
  payload: BusinessAtomicSavePayload,
): Promise<BusinessAtomicSaveResponse> {
  const response = await fetchWithAuth(`${apiEndpoint}/business/${businessId}/save`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  // Typed ApiError (keeps the server `detail` as the message, e.g. a 409 location conflict) rather
  // than a hand-written throw — required by the apis.ts api-error budget.
  await assertOk(response, 'Failed to save changes');
  return response.json();
}

export async function geocodeAddress(
  businessId: number,
  query: string,
  limit = 5,
): Promise<GeocodeResultRead[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/geocode?${params.toString()}`,
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to geocode address');
  }
  return response.json();
}

export async function reverseGeocode(
  businessId: number,
  lat: number,
  lng: number,
): Promise<GeocodeResultRead | null> {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/geocode/reverse?${params.toString()}`,
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to reverse-geocode coordinates');
  }
  return response.json();
}

export async function confirmBusinessLocation(
  businessId: number,
  locationUuid: string,
): Promise<BusinessLocationRead> {
  // PATCH with empty body — chokepoint flips source to 'user' on any update,
  // which clears the agent-detected "review me" badge.
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/${locationUuid}`,
    { method: 'PATCH', body: JSON.stringify({}) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to confirm location');
  }
  return response.json();
}

export async function triggerLocationDetect(
  businessId: number,
): Promise<LocationTaskTriggerResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/detect`,
    { method: 'POST' },
  );
  if (!response.ok) throw new Error('Failed to trigger detect');
  return response.json();
}

export async function triggerLocationBackfill(
  businessId: number,
): Promise<LocationTaskTriggerResponse> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/backfill`,
    { method: 'POST' },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? 'Failed to trigger backfill');
  }
  return response.json();
}

export async function fetchLocationTaskStatus(
  businessId: number,
  taskId: number,
): Promise<LocationTaskStatusRead> {
  const response = await fetchWithAuth(
    `${apiEndpoint}/business/${businessId}/locations/tasks/${taskId}`,
  );
  if (!response.ok) throw new Error('Failed to fetch task status');
  return response.json();
}

export const pinHeadline = async (
  headlineId: number,
  position: number | null,
): Promise<{ headline_id: number; pin_position: number | null }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/headlines/${headlineId}/pin`, {
    method: 'POST',
    body: JSON.stringify({ position }),
  });
  if (!response.ok) throw new Error(`Pin failed: ${response.status} ${response.statusText}`);
  return response.json();
};

// #5529: set/clear a headline's content type (brand/benefit/cta/...). Internal categorisation
// only — not synced to Google Ads.
export const setHeadlineType = async (
  headlineId: number,
  type: HeadlineType | null,
): Promise<{ headline_id: number; type: HeadlineType | null }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/headlines/${headlineId}/type`, {
    method: 'POST',
    body: JSON.stringify({ type }),
  });
  if (!response.ok) throw new Error(`Set headline type failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const pinKeyword = async (
  keywordId: number,
  position: number | null,
): Promise<{ keyword_id: number; pin_position: number | null }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/keywords/${keywordId}/pin`, {
    method: 'POST',
    body: JSON.stringify({ position }),
  });
  if (!response.ok) throw new Error(`Pin failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const lockKeyword = async (
  keywordId: number,
  isLocked: boolean,
): Promise<{ keyword_id: number; is_locked: boolean }> => {
  const response = await fetchWithAuth(`${apiEndpoint}/keywords/${keywordId}/lock`, {
    method: 'POST',
    body: JSON.stringify({ is_locked: isLocked }),
  });
  if (!response.ok) throw new Error(`Lock failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const pauseKeyword = async (
  campaignId: number,
  adId: number,
  keywordId: number,
): Promise<{ detail: string; keyword_id: number }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/keywords/${keywordId}/pause`,
    { method: 'POST' },
  );
  if (!response.ok) {
    // Surface the Google Ads rejection detail (a 502 from the live sync) rather than status text (#5755).
    const error = await response.json().catch(() => ({ detail: 'Failed to pause keyword' }));
    throw new Error(error.detail || 'Failed to pause keyword');
  }
  return response.json();
};

export const resumeKeyword = async (
  campaignId: number,
  adId: number,
  keywordId: number,
): Promise<{ detail: string; keyword_id: number }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/keywords/${keywordId}/resume`,
    { method: 'POST' },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to resume keyword' }));
    throw new Error(error.detail || 'Failed to resume keyword');
  }
  return response.json();
};

export const deleteKeyword = async (
  campaignId: number,
  adId: number,
  keywordId: number,
): Promise<{ detail: string; keyword_id: number }> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/keywords/${keywordId}`,
    { method: 'DELETE' },
  );
  if (!response.ok) throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
  return response.json();
};

export const updateKeyword = async (
  campaignId: number,
  adId: number,
  keywordId: number,
  patch: { text?: string; match_type?: string },
): Promise<{
  detail: string;
  keyword_id: number;
  old_keyword_id?: number;
  // Full recreated row (retire-and-recreate). Re-baseline local state from this
  // instead of guessing flags/casing, or Save Changes sticks dirty (#5859).
  keyword?: { id: number; text: string; match_type: string; is_paused: boolean; is_locked: boolean };
}> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/keywords/${keywordId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? `Update failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export const addKeywords = async (
  campaignId: number,
  adId: number,
  keywords: { text: string; match_type?: string }[],
): Promise<{
  detail: string;
  keywords: { id: number; text: string; match_type: string }[];
}> => {
  const response = await fetchWithAuth(
    `${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/keywords`,
    { method: 'POST', body: JSON.stringify({ keywords }) },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail ?? `Add failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

export async function getAccountLabels(): Promise<{ labels: AdLabel[] }> {
  const response = await fetchWithAuth(`${apiEndpoint}/labels`);
  if (!response.ok) throw new Error(`Failed to fetch account labels: ${response.status} ${response.statusText}`);
  return response.json();
}

export async function getAdLabels(campaignId: number, adId: number): Promise<{ labels: AdLabel[] }> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/labels`);
  if (!response.ok) throw new Error(`Failed to fetch ad labels: ${response.status} ${response.statusText}`);
  return response.json();
}

export async function addLabelToAd(campaignId: number, adId: number, labelName: string): Promise<AdLabel> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/labels`, {
    method: 'POST',
    body: JSON.stringify({ label_name: labelName }),
  });
  if (!response.ok) throw new Error(`Failed to add label: ${response.status} ${response.statusText}`);
  return response.json();
}

export async function removeLabelFromAd(campaignId: number, adId: number, labelId: string): Promise<void> {
  const response = await fetchWithAuth(`${apiEndpoint}/campaigns/${campaignId}/ads/${adId}/labels/${labelId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error(`Failed to remove label: ${response.status} ${response.statusText}`);
}

// ─── Unified Performance surface (#5971), ported to 1.13 for test-mode preview ───






// Point-in-time (NO_DATE), so no date params. The Landing Pages view rolls these
// up to URLs via keyword -> ad group -> ad final_url.


